import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CarFront,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  MapPinned,
  MessageCircle,
  PlaneLanding,
  ShieldCheck,
  Star,
  Users,
} from "lucide-react";
import Footer from "@/components/Footer";
import SeoJsonLd from "@/components/SeoJsonLd";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildLocalBusinessSchema,
  buildOrganizationSchema,
  buildPageSchemaGraph,
  buildServiceSchema,
} from "@/lib/seoSchemas";

const PAGE_URL = "https://getblackride.com/experience";
const META_TITLE = "BlackRide Experience | Premium Saudi & Uzbekistan Travel Packages";
const META_DESCRIPTION =
  "Plan curated premium travel experiences with BlackRide across Saudi Arabia and Uzbekistan. Umrah journeys, city tours, family holidays, business travel and chauffeur-led experiences.";
const OG_DESCRIPTION =
  "Curated premium journeys across Saudi Arabia and Uzbekistan with chauffeur-led travel, airport meet and greet, guided assistance, and refined destination planning.";
const OG_IMAGE = "https://getblackride.com/assets/images/experience/hero/hero-saudi-alula.jpg";
const WHATSAPP_NUMBER = "966540423401";
const HERO_AUTOPLAY_MS = 5000;

const HERO_MEDIA = {
  saudiAlula: "/assets/images/experience/hero/hero-saudi-alula.jpg",
  umrahMakkah: "/assets/images/experience/hero/hero-umrah-makkah.jpg",
  uzbekistanSamarkand: "/assets/images/experience/hero/hero-uzbekistan-samarkand.jpg",
  uzbekistanMountainsLake: "/assets/images/experience/hero/hero-uzbekistan-mountains-lake.jpg",
  executiveSaudiBusiness: "/assets/images/experience/hero/hero-executive-saudi-business.jpg",
};

const MEDIA = {
  hero: "/experience-media/photos/blackride-hero-chauffeur.jpg",
  saudi: "/experience-media/photos/blackride-saudi-experience.jpg",
  uzbekistan: "/experience-media/photos/blackride-uzbekistan-silk-road.jpg",
  arrival: "/experience-media/photos/blackride-airport-meet-greet.jpg",
  family: "/experience-media/photos/blackride-family-executive-travel.jpg",
  executive: "/experience-media/photos/blackride-business-travel.jpg",
  fleet: "/experience-media/photos/blackride-premium-fleet.jpg",
  curated: "/experience-media/photos/blackride-curated-experiences.jpg",
  localExpertise: "/experience-media/photos/blackride-local-expertise.jpg",
  concierge: "/experience-media/photos/blackride-whatsapp-concierge.jpg",
  umrah: "/experience-media/photos/blackride-premium-umrah-journey.jpg",
  silkRoad: "/experience-media/photos/blackride-uzbekistan-silk-road-escape.jpg",
  customJourney: "/experience-media/photos/blackride-private-journey.jpg",
  cta: "/experience-media/photos/blackride-final-cta-arrival.jpg",
};

const heroSlides = [
  {
    eyebrow: "PREMIUM TRAVEL EXPERIENCES",
    title: "Journeys That Stay With You, Long After You Return.",
    description: "Curated with care. Driven by comfort. Designed for those who expect more.",
    destinationLine: "Saudi Arabia. Uzbekistan. Unforgettable.",
    primaryCta: "Explore Saudi Arabia",
    primaryTarget: "saudi-experience",
    secondaryCta: "Explore Uzbekistan",
    secondaryTarget: "uzbekistan-experience",
    image: HERO_MEDIA.saudiAlula,
    alt: "Luxury Saudi Arabia travel experience at AlUla with chauffeur-led arrival",
    objectPosition: "80% center",
    previewTitle: "Saudi Arabia",
    previewSubtitle: "Timeless Wonders. Modern Comfort.",
    previewDescription: "Discover spirituality, heritage, and luxury across iconic destinations.",
  },
  {
    eyebrow: "PREMIUM UMRAH EXPERIENCE",
    title: "A Spiritual Journey, Planned With Care.",
    description:
      "From airport arrival to Makkah, Madinah, and every sacred stop in between, travel with comfort, privacy, and peace of mind.",
    destinationLine: "Umrah. Makkah. Madinah. Seamless.",
    primaryCta: "Explore Umrah Journey",
    primaryTarget: "saudi-experience",
    secondaryCta: "Plan My Experience",
    secondaryTarget: "enquire",
    image: HERO_MEDIA.umrahMakkah,
    alt: "Premium Umrah arrival experience in Makkah with private chauffeur support",
    objectPosition: "78% center",
    previewTitle: "Umrah Journey",
    previewSubtitle: "Spiritual. Peaceful. Seamless.",
    previewDescription: "A spiritual journey with comfort, privacy, and peace of mind.",
  },
  {
    eyebrow: "SILK ROAD EXPERIENCES",
    title: "Timeless Heritage, Curated For Modern Travellers.",
    description:
      "Explore Samarkand, Bukhara, and Tashkent through guided cultural journeys, private coordination, and premium travel comfort.",
    destinationLine: "Uzbekistan. Silk Road. Culture.",
    primaryCta: "Explore Uzbekistan",
    primaryTarget: "uzbekistan-experience",
    secondaryCta: "Plan My Journey",
    secondaryTarget: "enquire",
    image: HERO_MEDIA.uzbekistanSamarkand,
    alt: "Luxury cultural journey at Samarkand in Uzbekistan with private guide and chauffeur car",
    objectPosition: "82% center",
    previewTitle: "Uzbekistan",
    previewSubtitle: "Silk Road Heritage. Timeless Beauty.",
    previewDescription: "Explore ancient cities, vibrant culture, and breathtaking landscapes.",
  },
  {
    eyebrow: "NATURE & FAMILY ESCAPES",
    title: "Where Stillness Becomes The Luxury.",
    description:
      "From mountain air to crystal-clear lake views, discover Uzbekistan’s natural beauty through a journey designed around your pace.",
    destinationLine: "Mountains. Lakes. Adventure. Tranquility.",
    primaryCta: "Explore Nature Escape",
    primaryTarget: "uzbekistan-experience",
    secondaryCta: "Plan My Experience",
    secondaryTarget: "enquire",
    image: HERO_MEDIA.uzbekistanMountainsLake,
    alt: "Luxury family nature travel around mountains and lake landscapes in Uzbekistan",
    objectPosition: "76% center",
    previewTitle: "Mountains & Lakes",
    previewSubtitle: "Nature’s Escape. Pure Serenity.",
    previewDescription: "From snow-capped peaks to crystal-clear lakes, experience nature like never before.",
  },
  {
    eyebrow: "EXECUTIVE TRAVEL EXPERIENCES",
    title: "Business Travel, Elevated Beyond The Itinerary.",
    description:
      "For executives, delegations, and VIP guests, BlackRide Experience combines premium transport, local coordination, and discreet support.",
    destinationLine: "Riyadh. Jeddah. Dammam. Executive Ready.",
    primaryCta: "Explore Business Travel",
    primaryTarget: "enquire",
    secondaryCta: "Contact Concierge",
    secondaryMessage:
      "Hi BlackRide, I am interested in BlackRide Experience. Please help me plan a premium business travel journey in Saudi Arabia.",
    image: HERO_MEDIA.executiveSaudiBusiness,
    alt: "Executive business travel arrival in Riyadh with chauffeur-led premium transport",
    objectPosition: "80% center",
    previewTitle: "Executive Travel",
    previewSubtitle: "Discreet. Efficient. Premium.",
    previewDescription: "Corporate journeys supported by premium transport and concierge coordination.",
  },
];

const trustPoints = [
  { icon: CarFront, label: "Premium Chauffeur Fleet" },
  { icon: PlaneLanding, label: "Airport Meet & Greet" },
  { icon: MapPinned, label: "Curated Itineraries" },
  { icon: ShieldCheck, label: "Guided Assistance" },
  { icon: MessageCircle, label: "24/7 WhatsApp Concierge" },
  { icon: Users, label: "Trusted Local Partners" },
];

const destinationCards = [
  {
    eyebrow: "Saudi Arabia",
    title: "Premium Umrah & City Experience",
    subtitle: "Umrah • Heritage • Culture • Business",
    description:
      "Spiritual journeys, historic cities, modern luxury, and private chauffeur-led travel across Jeddah, Makkah, Madinah, Riyadh, and beyond.",
    badges: ["6 Days", "Private Chauffeur", "Family Friendly"],
    image: MEDIA.saudi,
    alt: "Premium Saudi Arabia destination experience with chauffeur-led travel",
    cta: "Explore Saudi Experience",
    href: "#saudi-experience",
  },
  {
    eyebrow: "Uzbekistan",
    title: "Silk Road & Heritage Journey",
    subtitle: "Silk Road • Mountains • Culture • Food",
    description:
      "Timeless cities, breathtaking landscapes, rich cuisine, and guided cultural experiences across Tashkent, Samarkand, Bukhara, Amirsoy, and Charvak.",
    badges: ["5 Days", "Guided Tours", "Family Friendly"],
    image: MEDIA.uzbekistan,
    alt: "Luxury Uzbekistan destination journey inspired by Samarkand and Silk Road heritage",
    cta: "Explore Uzbekistan Experience",
    href: "#uzbekistan-experience",
  },
];

const whyCards = [
  {
    title: "Premium Fleet",
    description:
      "Executive sedans, family-ready SUVs, premium vans, and larger comfort vehicles selected around guest profile, luggage, and route style.",
    image: MEDIA.fleet,
    alt: "BlackRide premium fleet for curated journeys in Saudi Arabia",
  },
  {
    title: "Airport Meet & Greet",
    description:
      "A composed arrival experience with meet-and-greet support, luggage handling, and a calmer handoff from terminal to vehicle.",
    image: MEDIA.arrival,
    alt: "Premium airport arrival and meet and greet experience",
  },
  {
    title: "Curated Experiences",
    description:
      "Destination-led itineraries designed around the traveller, not around a generic package grid or rushed group schedule.",
    image: MEDIA.curated,
    alt: "Curated premium travel experience across Saudi Arabia",
  },
  {
    title: "Local Expertise",
    description:
      "Trusted local coordination, practical route planning, and destination support that makes movement between cities feel polished and predictable.",
    image: MEDIA.localExpertise,
    alt: "Guided cultural travel support in Uzbekistan",
  },
  {
    title: "24/7 Support",
    description:
      "WhatsApp-led coordination for arrival updates, itinerary adjustments, vehicle planning, and the small details that matter on premium trips.",
    image: MEDIA.concierge,
    alt: "Executive travel support and discreet concierge planning",
  },
  {
    title: "Family & Executive Friendly",
    description:
      "Suitable for business owners, hosted guests, GCC families, and travellers who value privacy, timing, and smoother daily movement.",
    image: MEDIA.family,
    alt: "Family-friendly private travel experience with premium support",
  },
];

const featuredExperiences = [
  {
    tag: "Popular",
    title: "Premium Umrah Journey",
    description:
      "A curated Saudi journey connecting airport arrival, Makkah, Madinah, ziyarah-style movement, and polished intercity transport.",
    image: MEDIA.umrah,
    cta: "View Saudi Journey",
    href: "#saudi-experience",
  },
  {
    tag: "Family Favourite",
    title: "Uzbekistan Silk Road Escape",
    description:
      "A richly paced itinerary across Tashkent, Samarkand, Bukhara, mountains, and lakes with guided local assistance.",
    image: MEDIA.silkRoad,
    cta: "View Uzbekistan Journey",
    href: "#uzbekistan-experience",
  },
  {
    tag: "Business",
    title: "Executive Saudi Business Journey",
    description:
      "A polished business-led travel format for hosted executives, founders, and premium guests who need seamless movement between airport, hotel, and meetings.",
    image: MEDIA.executive,
    cta: "Discuss Business Travel",
    href: "#enquire",
  },
  {
    tag: "Custom",
    title: "Custom Private Journey",
    description:
      "A private experience designed around your cities, timing, family profile, hotel preference, and the tone of the journey you want to create.",
    image: MEDIA.customJourney,
    cta: "Request a Curated Plan",
    href: "#enquire",
  },
];

const saudiTimeline = [
  {
    day: "Day 1",
    title: "Arrival in Jeddah & Transfer to Makkah",
    image: "/experience-media/photos/blackride-jeddah-arrival-makkah-transfer.jpg",
    alt: "Premium Jeddah airport arrival and private transfer to Makkah",
    items: [
      "Airport meet and greet",
      "Luggage assistance",
      "Premium BlackRide vehicle",
      "Private Jeddah Airport to Makkah transfer",
    ],
  },
  {
    day: "Day 2",
    title: "Makkah Ziyarah & Local Experience",
    image: "/experience-media/photos/blackride-makkah-ziyarah.jpg",
    alt: "Premium Makkah ziyarah experience with private family travel",
    items: [
      "Chauffeur support in Makkah",
      "Ziyarah locations subject to local access and timing",
      "Flexible family movement",
    ],
  },
  {
    day: "Day 3",
    title: "Makkah to Madinah Transfer",
    image: "/experience-media/photos/blackride-makkah-madinah-transfer.jpg",
    alt: "Private premium transfer from Makkah to Madinah",
    items: [
      "Private intercity transfer",
      "Spacious vehicle for family and luggage",
      "Comfortable scheduled travel",
    ],
  },
  {
    day: "Day 4",
    title: "Madinah Ziyarah",
    image: "/experience-media/photos/blackride-madinah-ziyarah.jpg",
    alt: "Madinah historical and ziyarah experience with private chauffeur support",
    items: [
      "Local ziyarah coordination if arranged",
      "Chauffeur-led movement",
      "Important landmarks subject to access",
    ],
  },
  {
    day: "Day 5",
    title: "Madinah to Jeddah Transfer",
    image: "/experience-media/photos/blackride-madinah-jeddah-transfer.jpg",
    alt: "Private intercity transfer from Madinah to Jeddah",
    items: [
      "Private transfer to Jeddah",
      "Optional comfort stops where practical",
      "Hotel handoff or city arrival support",
    ],
  },
  {
    day: "Day 6",
    title: "Jeddah City Experience & Departure",
    image: "/experience-media/photos/blackride-jeddah-city-departure.jpg",
    alt: "Jeddah city experience and departure transfer with BlackRide",
    items: [
      "Optional Al Balad, Waterfront, shopping, or dining",
      "Flexible city timing",
      "Final airport transfer",
    ],
  },
];

const uzbekistanTimeline = [
  {
    day: "Day 1",
    title: "Tashkent Arrival & Evening City Experience",
    items: [
      "Airport meet and greet",
      "Hotel transfer",
      "Traditional Uzbek lunch",
      "Tashkent City Mall and Park",
      "Wax Museum",
      "Light dinner or local food stop",
    ],
  },
  {
    day: "Day 2",
    title: "Amirsoy, Chimgan & Charvak Lake",
    items: [
      "Amirsoy Snow Resort",
      "Scenic cable car",
      "Mountain activities",
      "Charvak Lake views",
      "Ice City or Seoul Mun evening experience",
    ],
  },
  {
    day: "Day 3",
    title: "Samarkand Heritage Tour",
    items: [
      "Train from Tashkent to Samarkand",
      "Gur-e-Amir Mausoleum",
      "Registan Square",
      "Bibi Khanum Mosque",
      "Siyob Bazaar",
      "Shah-i-Zinda",
      "Silk Road Samarkand",
      "Transfer to Bukhara",
    ],
  },
  {
    day: "Day 4",
    title: "Bukhara Historical Experience",
    items: [
      "Bolo Hauz Mosque",
      "Sitora-i-Mohi Khosa",
      "Baha-ud-Din Naqshband Complex",
      "Old Bukhara",
      "Lyabi Hauz",
      "Kalon Minaret",
      "Complimentary dinner grills by HorizonCrew",
      "Late train to Tashkent",
    ],
  },
  {
    day: "Day 5",
    title: "Tashkent City Tour & Departure",
    items: [
      "Hazrat Imam Complex",
      "Chorsu Bazaar",
      "Minor White Mosque",
      "Central Park",
      "Tashkent TV Tower optional",
      "Magic City",
      "Airport transfer",
    ],
  },
];

const fleetCards = [
  {
    name: "BMW 735i",
    useCase: "Executive Travel",
    image: "/experience-media/photos/blackride-bmw-735i.jpg",
    note: "Flagship sedan for hosted arrivals, senior leadership travel, and refined city movement.",
  },
  {
    name: "GMC Yukon XL",
    useCase: "VIP Groups",
    image: "/experience-media/photos/blackride-gmc-yukon-xl.jpg",
    note: "A strong fit for family arrivals, premium airport runs, and guests with more luggage and privacy needs.",
  },
  {
    name: "Mercedes Vito",
    useCase: "Family Travel",
    image: "/experience-media/photos/blackride-mercedes-vito.jpg",
    note: "Comfortable van travel for families and smaller private groups who prefer room to move quietly.",
  },
  {
    name: "Hyundai Staria",
    useCase: "Premium Comfort",
    image: "/experience-media/photos/blackride-hyundai-staria.jpg",
    note: "A polished modern option for guests who want more space without moving to a larger group vehicle.",
  },
  {
    name: "Toyota Hiace",
    useCase: "Group Travel",
    image: "/experience-media/photos/blackride-toyota-hiace.jpg",
    note: "Useful for delegations, larger family circles, and scheduled movement with additional luggage capacity.",
  },
  {
    name: "Lexus ES",
    useCase: "Business Travel",
    image: "/experience-media/photos/blackride-lexus-es.jpg",
    note: "Understated executive comfort for hotel transfers, hosted meetings, and calm city schedules.",
  },
];

const comparisonRows = [
  ["Last-minute transport", "Pre-arranged chauffeur coordination"],
  ["Generic itinerary flow", "Curated journey planning"],
  ["Crowded group movement", "Private and family-friendly travel"],
  ["Limited support", "WhatsApp-based coordination"],
  ["Uncertain vehicle quality", "Premium fleet standards"],
  ["Fragmented planning", "End-to-end experience design"],
];

const processSteps = [
  "Share your destination and travel dates",
  "Tell us your preferences",
  "Receive a curated experience plan",
  "Confirm itinerary and inclusions",
  "Arrive and travel seamlessly",
];

const experienceTypes = [
  "Premium Family Experience",
  "Executive Business Travel Experience",
  "Umrah & Ziyarah Experience",
  "Corporate Group Experience",
  "Custom Private Journey",
];

const faqs = [
  {
    question: "What is BlackRide Experience?",
    answer:
      "BlackRide Experience is a premium travel coordination offering that combines chauffeur-led transport, curated itineraries, airport meet and greet support, destination assistance, and trusted local coordination for guests who want a more seamless journey.",
  },
  {
    question: "Is BlackRide Experience only for Saudi Arabia?",
    answer:
      "No. BlackRide Experience currently covers Saudi Arabia and Uzbekistan. Saudi journeys are supported by BlackRide’s premium chauffeur services, while the Uzbekistan itinerary is designed and hosted by HorizonCrew Uzbekistan with on-ground coordination and guided assistance.",
  },
  {
    question: "Can I book an Umrah travel experience?",
    answer:
      "Yes. Guests can enquire about a premium Umrah and ziyarah-style experience built around airport arrival, private intercity movement, and curated daily travel planning in Saudi Arabia.",
  },
  {
    question: "Does BlackRide provide airport transfers in Saudi Arabia?",
    answer:
      "Yes. Airport arrivals, private intercity transfers, and premium local movement are core parts of the Saudi Arabia experience format.",
  },
  {
    question: "Can I customise the itinerary?",
    answer:
      "Yes. Both Saudi Arabia and Uzbekistan experiences can be tailored around travel month, guest count, hotel preference, route priorities, and the overall pace of the journey.",
  },
  {
    question: "Who operates the Uzbekistan itinerary?",
    answer:
      "The Uzbekistan itinerary is designed and hosted by HorizonCrew Uzbekistan with on-ground coordination and guided assistance throughout the journey.",
  },
  {
    question: "Are flights included?",
    answer:
      "Flights should not be assumed to be included. Final itinerary scope, inclusions, and coordination details are confirmed during enquiry.",
  },
  {
    question: "Are hotels included?",
    answer:
      "Hotel category and accommodation arrangements depend on the final confirmed package. They are discussed and confirmed during the enquiry process rather than assumed upfront.",
  },
  {
    question: "Are visas included?",
    answer:
      "Visa processing or approvals should not be assumed. Travellers should verify their own visa requirements through relevant official channels before confirming travel plans.",
  },
  {
    question: "Can families book private tours?",
    answer:
      "Yes. The experience is particularly suitable for families who want private movement, better luggage planning, calmer daily routing, and a more comfortable travel rhythm.",
  },
  {
    question: "Can business travellers book premium travel experiences?",
    answer:
      "Yes. BlackRide Experience is designed for hosted executives, founders, VIP guests, and business groups who want a more polished arrival and movement standard.",
  },
  {
    question: "How do I enquire?",
    answer:
      "You can enquire by completing the form on this page or by sending a WhatsApp message with your destination, dates, guest count, and preferences. Final itinerary, inclusions, hotel category, guide availability, activities, train class, entries, meals, pricing, and travel dates are confirmed during enquiry.",
  },
];

const PAGE_COPY = {
  en: {
    home: "Home",
    experience: "BlackRide Experience",
    languageLabel: "Page language",
    trust: {
      kicker: "Trusted by Travellers",
      body: "Crafted for guests who expect privacy, clarity, and polished movement.",
    },
    rating: {
      title: "4.9/5 Service Excellence",
      subtitle: "Configured luxury trust signal",
    },
    destinationIntro: {
      eyebrow: "Explore Our Destinations",
      title: "Destination journeys shaped around how premium travellers actually move",
      body: "BlackRide Experience is designed as a destination planner rather than a package catalogue. Each itinerary begins with the guest, then layers together airport movement, route logic, premium transport, local rhythm, and the kind of support that makes travel feel noticeably smoother.",
    },
    whyIntro: {
      eyebrow: "Why Travel With BlackRide Experience?",
      title: "A more considered standard for destination travel",
      body: "For guests who value privacy, timing, and premium coordination, the difference is often in the details: the arrival tone, the vehicle choice, the handoff between cities, the clarity of the plan, and the quality of the support around it.",
    },
    featuredIntro: {
      eyebrow: "Featured Experiences",
      title: "Journeys designed around purpose, not templates",
      body: "Some guests arrive for Umrah, some for private holidays, some for business, and some for a journey that blends several priorities at once. These featured formats give a sense of the experience directions BlackRide can shape.",
    },
    saudiIntro: {
      eyebrow: "Saudi Arabia Experience",
      title: "Premium Umrah & City Journey",
      body: "A seamless 6-day journey across Jeddah, Makkah, and Madinah with premium chauffeur service at every step. Designed for guests who want a more composed Saudi itinerary built around airport arrival, spiritual travel, private intercity movement, and elegant city timing.",
    },
    saudiCta: {
      kicker: "Private Saudi Planning",
      title: "From airport arrival to final departure, every movement feels more settled.",
      body: "BlackRide supports Saudi experiences through premium airport pickup, family-ready vehicle planning, intercity transfer coordination, and a calmer way to move between Makkah, Madinah, and Jeddah.",
      primary: "Plan My Saudi Journey",
      secondary: "View Full Saudi Experience",
    },
    uzbekIntro: {
      eyebrow: "Uzbekistan Experience",
      title: "Uzbekistan 5 Days / 5 Nights Premium Family Itinerary",
      body: "Tashkent • Samarkand • Bukhara • Mountains & Lake Tour. This itinerary is designed and hosted by HorizonCrew Uzbekistan with on-ground coordination and guided assistance throughout the journey.",
      note: "Dates to be confirmed. Itinerary is customisable based on flights, group size, hotel preference, and guest requirements.",
      kicker: "Hosted by HorizonCrew Uzbekistan",
      ctaTitle: "A destination-led cultural journey with local rhythm already built in.",
      ctaBody: "From Tashkent arrivals and mountain days to Silk Road cities and family-friendly cultural pacing, the Uzbekistan experience is presented through BlackRide as a premium curated option and hosted on the ground by HorizonCrew Uzbekistan.",
      primary: "Plan My Uzbekistan Journey",
      secondary: "Request Custom Dates",
    },
    fleetIntro: {
      eyebrow: "Travel in Comfort and Style",
      title: "A fleet shaped around the traveller, not the other way around",
      body: "The vehicle matters because the day feels different inside it. Families need room, executives need privacy, arrivals need calm, and intercity movement needs the right balance of space, support, and premium presentation.",
    },
    differenceIntro: {
      eyebrow: "The BlackRide Experience Difference",
      title: "Luxury travel becomes easier when the movement is already solved",
      body: "Most travel friction appears in the gaps between moments: the arrival, the transfer, the luggage, the wrong vehicle, the missed timing, the fragmented support. BlackRide Experience is designed to close those gaps elegantly.",
      standard: "Standard travel",
      elevated: "BlackRide Experience",
    },
    howItWorks: {
      eyebrow: "How It Works",
      title: "Five calm steps from first enquiry to final arrival",
      body: "The planning flow stays simple on purpose so the journey can become more tailored without becoming complicated for the guest.",
    },
    enquire: {
      eyebrow: "Let’s Curate Your Next Journey",
      title: "Share the shape of the trip. We’ll shape the experience around it.",
      body: "Tell us where you want to travel, how many guests are moving, and what kind of experience you want to create. We’ll use that to guide the next conversation and refine the itinerary.",
      bullets: [
        "Suitable for premium family journeys, executive schedules, Umrah-led travel, and hosted private itineraries.",
        "Final itinerary, inclusions, hotel category, guide availability, activities, train class, entries, meals, pricing, and travel dates are confirmed during enquiry.",
        "Flights, visas, and hotel arrangements should not be assumed unless specifically confirmed as part of the final plan.",
      ],
      whatsapp: "WhatsApp Concierge",
      email: "Email Your Request",
      fullName: "Full Name",
      whatsappNumber: "WhatsApp Number",
      emailLabel: "Email",
      destination: "Destination",
      travelMonth: "Travel Month",
      guests: "Number of Guests",
      experienceType: "Preferred Experience Type",
      requirements: "Message / Requirements",
      travelMonthPlaceholder: "Example: September 2026",
      requirementsPlaceholder: "Share your route, guest profile, preferred hotels, business schedule, or any special requirements.",
      submit: "Request a Curated Experience",
      chat: "Chat on WhatsApp",
    },
    faqIntro: {
      eyebrow: "Frequently Asked Questions",
      title: "A few useful details before you enquire",
      body: "The exact shape of the journey is always confirmed around the guest, but these answers cover the most common planning questions.",
    },
    relatedIntro: {
      eyebrow: "Related BlackRide Services",
      title: "Continue planning the Saudi side of the journey",
      body: "If your experience includes Saudi Arabia, these pages help you understand the route logic, airport movement, and chauffeur-led service options in more detail.",
    },
    finalCta: {
      title: "Your journey should feel effortless from the moment you land.",
      body: "Whether you are visiting Saudi Arabia for Umrah, business, or leisure — or discovering the timeless beauty of Uzbekistan — BlackRide Experience curates every journey around comfort, privacy, and seamless coordination.",
      button: "Plan My Experience",
    },
    stickyCta: "Plan My Experience",
  },
  ar: {
    home: "الرئيسية",
    experience: "تجارب بلاك رايد",
    languageLabel: "لغة الصفحة",
    trust: {
      kicker: "موثوق من المسافرين",
      body: "مصمم للضيوف الذين يقدّرون الخصوصية والوضوح والتنقل المصقول.",
    },
    rating: { title: "4.9/5 تميّز في الخدمة", subtitle: "إشارة ثقة قابلة للضبط" },
    destinationIntro: {
      eyebrow: "استكشف الوجهات",
      title: "رحلات وجهات مصممة وفقاً لكيفية تنقل المسافرين المميزين فعلياً",
      body: "تم تصميم بلاك رايد إكسبيرينس كمخطط وجهات لا ككتالوج باقات. تبدأ كل رحلة بالضيف ثم تُنسج حولها حركة المطار، ومنطق المسار، والنقل الفاخر، وإيقاع الوجهة، والدعم الذي يجعل السفر أكثر سلاسة.",
    },
    whyIntro: {
      eyebrow: "لماذا السفر مع بلاك رايد إكسبيرينس؟",
      title: "معيار أكثر وعياً لسفر الوجهات",
      body: "بالنسبة للضيوف الذين يقدّرون الخصوصية والتوقيت والتنسيق الراقي، يكمن الفرق غالباً في التفاصيل: نبرة الوصول، واختيار المركبة، والانتقال بين المدن، ووضوح الخطة، وجودة الدعم المحيط بها.",
    },
    featuredIntro: {
      eyebrow: "تجارب مميزة",
      title: "رحلات صُممت حول الغاية لا القوالب الجاهزة",
      body: "بعض الضيوف يصلون لأداء العمرة، وبعضهم لعطلة خاصة، وبعضهم للعمل، وآخرون لرحلة تجمع بين أكثر من أولوية. تمنح هذه النماذج فكرة عن اتجاهات التجارب التي يمكن لبلاك رايد تشكيلها.",
    },
    saudiIntro: {
      eyebrow: "تجربة المملكة العربية السعودية",
      title: "رحلة عمرة ومدينة فاخرة",
      body: "رحلة سلسة لمدة 6 أيام عبر جدة ومكة والمدينة مع خدمة سائق فاخر في كل خطوة. صُممت للضيوف الذين يريدون برنامجاً سعودياً أكثر هدوءاً يعتمد على الوصول عبر المطار، والسفر الروحاني، والتنقل الخاص بين المدن، وإيقاع المدينة الأنيق.",
    },
    saudiCta: {
      kicker: "تخطيط سعودي خاص",
      title: "من الوصول إلى المطار حتى المغادرة الأخيرة، تبدو كل حركة أكثر استقراراً.",
      body: "تدعم بلاك رايد التجارب السعودية من خلال الاستقبال الفاخر من المطار، وتخطيط المركبات المناسبة للعائلات، وتنسيق التنقل بين المدن، وطريقة أكثر هدوءاً للحركة بين مكة والمدينة وجدة.",
      primary: "خطط رحلتي السعودية",
      secondary: "عرض التجربة السعودية الكاملة",
    },
    uzbekIntro: {
      eyebrow: "تجربة أوزبكستان",
      title: "برنامج عائلي فاخر 5 أيام / 5 ليالٍ في أوزبكستان",
      body: "طشقند • سمرقند • بخارى • الجبال والبحيرة. تم تصميم هذا البرنامج واستضافته بواسطة HorizonCrew Uzbekistan مع تنسيق ميداني ومساعدة إرشادية طوال الرحلة.",
      note: "يتم تأكيد التواريخ لاحقاً. ويمكن تخصيص البرنامج وفقاً للرحلات الجوية وعدد الضيوف وفئة الفندق ومتطلبات المسافر.",
      kicker: "تستضيفه HorizonCrew Uzbekistan",
      ctaTitle: "رحلة ثقافية تقودها الوجهة مع إيقاع محلي جاهز مسبقاً.",
      ctaBody: "من الوصول إلى طشقند وأيام الجبال إلى مدن طريق الحرير والإيقاع الثقافي المناسب للعائلات، تُقدم تجربة أوزبكستان عبر بلاك رايد كخيار فاخر منسق ويستضيفها على الأرض HorizonCrew Uzbekistan.",
      primary: "خطط رحلتي إلى أوزبكستان",
      secondary: "اطلب مواعيد مخصصة",
    },
    fleetIntro: {
      eyebrow: "سافر براحة وأناقة",
      title: "أسطول يتشكل حول المسافر، وليس العكس",
      body: "المركبة مهمة لأن اليوم يبدو مختلفاً بداخلها. العائلات تحتاج إلى المساحة، والتنفيذيون يحتاجون إلى الخصوصية، والوصول يحتاج إلى الهدوء، والتنقل بين المدن يحتاج إلى التوازن الصحيح بين المساحة والدعم والتقديم الفاخر.",
    },
    differenceIntro: {
      eyebrow: "الفرق مع بلاك رايد إكسبيرينس",
      title: "يصبح السفر الفاخر أسهل عندما يكون التنقل محلولاً مسبقاً",
      body: "يظهر معظم احتكاك السفر في الفجوات بين اللحظات: الوصول، التحويل، الأمتعة، المركبة غير المناسبة، التوقيت المفقود، والدعم المجزأ. صُممت بلاك رايد إكسبيرينس لإغلاق هذه الفجوات بأناقة.",
      standard: "السفر الاعتيادي",
      elevated: "بلاك رايد إكسبيرينس",
    },
    howItWorks: {
      eyebrow: "كيف يعمل",
      title: "خمس خطوات هادئة من أول استفسار حتى آخر وصول",
      body: "يبقى تدفق التخطيط بسيطاً عمداً حتى تصبح الرحلة أكثر تخصيصاً دون أن تصبح معقدة على الضيف.",
    },
    enquire: {
      eyebrow: "دعنا نصمم رحلتك القادمة",
      title: "شارك ملامح الرحلة، وسنصمم التجربة حولها.",
      body: "أخبرنا إلى أين تريد السفر، وعدد الضيوف، ونوع التجربة التي تريدها. سنستخدم ذلك لتوجيه المحادثة التالية وصقل البرنامج.",
      bullets: [
        "مناسب للرحلات العائلية الراقية، والجداول التنفيذية، والسفر المرتبط بالعمرة، والمسارات الخاصة المستضافة.",
        "يتم تأكيد البرنامج النهائي، وما يشمله، وفئة الفندق، وتوفر المرشد، والأنشطة، ودرجة القطار، والدخول، والوجبات، والتسعير، وتواريخ السفر أثناء الاستفسار.",
        "لا ينبغي افتراض الرحلات الجوية أو التأشيرات أو ترتيبات الفنادق إلا إذا تم تأكيدها صراحة ضمن الخطة النهائية.",
      ],
      whatsapp: "كونسيرج واتساب",
      email: "أرسل طلبك عبر البريد",
      fullName: "الاسم الكامل",
      whatsappNumber: "رقم واتساب",
      emailLabel: "البريد الإلكتروني",
      destination: "الوجهة",
      travelMonth: "شهر السفر",
      guests: "عدد الضيوف",
      experienceType: "نوع التجربة المفضل",
      requirements: "الرسالة / المتطلبات",
      travelMonthPlaceholder: "مثال: سبتمبر 2026",
      requirementsPlaceholder: "شاركنا المسار، ونوع الضيوف، والفنادق المفضلة، وجدول الأعمال، أو أي متطلبات خاصة.",
      submit: "اطلب تجربة منسقة",
      chat: "الدردشة عبر واتساب",
    },
    faqIntro: {
      eyebrow: "الأسئلة الشائعة",
      title: "بعض التفاصيل المفيدة قبل الاستفسار",
      body: "يتم دائماً تأكيد شكل الرحلة حول الضيف، لكن هذه الإجابات تغطي أكثر أسئلة التخطيط شيوعاً.",
    },
    relatedIntro: {
      eyebrow: "خدمات بلاك رايد ذات الصلة",
      title: "واصل التخطيط للجانب السعودي من الرحلة",
      body: "إذا كانت تجربتك تشمل المملكة العربية السعودية، فستساعدك هذه الصفحات على فهم منطق المسارات، وحركة المطار، وخيارات الخدمة المعتمدة على السائق بمزيد من التفصيل.",
    },
    finalCta: {
      title: "يجب أن تبدو رحلتك سهلة منذ اللحظة التي تهبط فيها.",
      body: "سواء كنت تزور المملكة العربية السعودية للعمرة أو العمل أو الترفيه — أو تكتشف جمال أوزبكستان الخالد — فإن بلاك رايد إكسبيرينس تصمم كل رحلة حول الراحة والخصوصية والتنسيق السلس.",
      button: "خطط تجربتي",
    },
    stickyCta: "خطط تجربتي",
  },
  uz: {
    home: "Bosh sahifa",
    experience: "BlackRide Experience",
    languageLabel: "Sahifa tili",
    trust: {
      kicker: "Sayohatchilar ishonchi",
      body: "Maxfiylik, aniqlik va puxta harakatni kutadigan mehmonlar uchun yaratilgan.",
    },
    rating: { title: "4.9/5 xizmat sifati", subtitle: "Moslashtiriladigan ishonch belgisi" },
    destinationIntro: {
      eyebrow: "Yo‘nalishlarni kashf eting",
      title: "Premium sayohatchilar haqiqatan qanday harakat qilsa, shunga mos yo‘nalishlar",
      body: "BlackRide Experience oddiy paketlar katalogi emas, balki puxta o‘ylangan manzil rejalashtiruvchisidir. Har bir safar mehmondan boshlanadi va keyin aeroport transferi, yo‘l mantig‘i, premium transport va mahalliy ritm atrofida quriladi.",
    },
    whyIntro: {
      eyebrow: "Nega BlackRide Experience bilan sayohat qilish kerak?",
      title: "Manzilga yo‘naltirilgan sayohat uchun yanada puxta standart",
      body: "Maxfiylik, vaqt va premium muvofiqlashtirishni qadrlaydigan mehmonlar uchun farq aynan tafsilotlarda seziladi: kutib olish ohangi, avtomobil tanlovi, shaharlar orasidagi uzluksizlik va rejadagi aniqlik.",
    },
    featuredIntro: {
      eyebrow: "Tavsiya etilgan tajribalar",
      title: "Shablon emas, maqsad atrofida qurilgan safarlar",
      body: "Kimdir Umra uchun, kimdir xususiy dam olish, kimdir ish yuzasidan, boshqalar esa bir nechta ustuvor yo‘nalishni birlashtirgan safar uchun keladi. Bu formatlar BlackRide shakllantira oladigan safar turlarini ko‘rsatadi.",
    },
    saudiIntro: {
      eyebrow: "Saudiya Arabistoni tajribasi",
      title: "Premium Umra va shahar safari",
      body: "Jidda, Makkah va Madina bo‘ylab 6 kunlik silliq safar. Aeroportdan kutib olish, ma’naviy safar, xususiy shaharlararo harakat va puxta vaqt boshqaruvi bilan yuqori darajadagi Saudiya marshruti uchun yaratilgan.",
    },
    saudiCta: {
      kicker: "Xususiy Saudiya rejalashtiruvi",
      title: "Aeroportga qo‘ngandan oxirgi jo‘nashgacha har bir harakat xotirjam bo‘ladi.",
      body: "BlackRide Saudiya safarlarida premium aeroport kutib olish, oilaviy avtomobil tanlovi, shaharlararo transfer va Makkah, Madina hamda Jidda o‘rtasida yanada sokin harakatni qo‘llab-quvvatlaydi.",
      primary: "Saudiya safarimni rejalashtirish",
      secondary: "To‘liq Saudiya tajribasini ko‘rish",
    },
    uzbekIntro: {
      eyebrow: "O‘zbekiston tajribasi",
      title: "O‘zbekiston 5 kun / 5 tunlik premium oilaviy yo‘nalishi",
      body: "Toshkent • Samarqand • Buxoro • Tog‘lar va ko‘l safari. Ushbu marshrut HorizonCrew Uzbekistan tomonidan ishlab chiqilgan va yerda to‘liq muvofiqlashtiriladi.",
      note: "Sanalar keyinroq tasdiqlanadi. Marshrut parvozlar, mehmonlar soni, mehmonxona darajasi va talablaringizga qarab moslashtiriladi.",
      kicker: "HorizonCrew Uzbekistan tomonidan olib boriladi",
      ctaTitle: "Mahalliy ritmi oldindan shakllangan madaniy safar.",
      ctaBody: "Toshkentga kelishdan tortib tog‘ sayohatlari va Ipak yo‘li shaharlari bo‘ylab oilaviy madaniy ritmgacha, O‘zbekiston tajribasi BlackRide orqali premium kuratsiyalangan variant sifatida taqdim etiladi va joyida HorizonCrew Uzbekistan tomonidan boshqariladi.",
      primary: "O‘zbekiston safarimni rejalashtirish",
      secondary: "Mos sanalarni so‘rash",
    },
    fleetIntro: {
      eyebrow: "Qulaylik va uslubda sayohat qiling",
      title: "Mehmon atrofida shakllangan avtopark",
      body: "Avtomobil muhim, chunki kun ichidagi hissiyot aynan shunda o‘zgaradi. Oilaga kenglik, rahbarlarga maxfiylik, qo‘nishga xotirjamlik, shaharlararo yo‘lga esa to‘g‘ri muvozanat kerak.",
    },
    differenceIntro: {
      eyebrow: "BlackRide Experience farqi",
      title: "Harakat oldindan hal qilinganda luxury sayohat osonlashadi",
      body: "Sayohatdagi asosiy noqulayliklar aynan oraliq lahzalarda paydo bo‘ladi: kutib olish, transfer, yuk, noto‘g‘ri avtomobil, uzilgan yordam. BlackRide Experience shu bo‘shliqlarni nafis tarzda yopish uchun yaratilgan.",
      standard: "Oddiy sayohat",
      elevated: "BlackRide Experience",
    },
    howItWorks: {
      eyebrow: "Qanday ishlaydi",
      title: "Birinchi murojaatdan yakuniy yetib borishgacha besh sokin qadam",
      body: "Rejalashtirish jarayoni ataylab sodda qoldiriladi, shunda safar murakkablashmasdan ko‘proq shaxsiylashtiriladi.",
    },
    enquire: {
      eyebrow: "Keyingi safaringizni birga shakllantiraylik",
      title: "Safar rejangizni ayting, biz tajribani shunga moslaymiz.",
      body: "Qayerga ketayotganingizni, nechta mehmon borligini va qanday tajriba xohlayotganingizni bildiring. Keyingi suhbatni shunga qarab olib boramiz.",
      bullets: [
        "Premium oilaviy safarlar, biznes delegatsiyalari, Umra safarlari va xususiy marshrutlar uchun mos.",
        "Yakuniy marshrut, xizmatlar, mehmonxona darajasi, gid mavjudligi, faoliyatlar, poyezd klassi, kirish chiptalari, ovqatlanish, narx va sanalar so‘rov davomida tasdiqlanadi.",
        "Parvozlar, vizalar va mehmonxona bandlari yakuniy reja doirasida alohida tasdiqlanmaguncha kiritilgan deb hisoblanmaydi.",
      ],
      whatsapp: "WhatsApp concierge",
      email: "So‘rovni email orqali yuborish",
      fullName: "To‘liq ism",
      whatsappNumber: "WhatsApp raqami",
      emailLabel: "Email",
      destination: "Yo‘nalish",
      travelMonth: "Safar oyi",
      guests: "Mehmonlar soni",
      experienceType: "Istalgan tajriba turi",
      requirements: "Xabar / talablar",
      travelMonthPlaceholder: "Masalan: Sentabr 2026",
      requirementsPlaceholder: "Yo‘nalish, mehmon profili, afzal mehmonxonalar, biznes jadvali yoki maxsus talablaringizni yozing.",
      submit: "Kuratsiyalangan tajriba so‘rash",
      chat: "WhatsApp orqali yozish",
    },
    faqIntro: {
      eyebrow: "Ko‘p so‘raladigan savollar",
      title: "So‘rov yuborishdan oldin bir nechta foydali tafsilotlar",
      body: "Safarning yakuniy shakli har doim mehmon atrofida belgilanadi, ammo bu javoblar eng ko‘p uchraydigan savollarni qamrab oladi.",
    },
    relatedIntro: {
      eyebrow: "Tegishli BlackRide xizmatlari",
      title: "Saudiya qismi bo‘yicha rejalashtirishni davom ettiring",
      body: "Agar tajribangiz Saudiya Arabistonini ham o‘z ichiga olsa, bu sahifalar aeroport harakati, marshrut mantig‘i va chauffeur-led xizmatlarni yaxshiroq tushunishga yordam beradi.",
    },
    finalCta: {
      title: "Safaringiz qo‘ngan zahotingizdan boshlab yengil his qilinishi kerak.",
      body: "Saudiya Arabistoniga Umra, biznes yoki dam olish uchun tashrif buyurasizmi — yoki O‘zbekistonning betakror go‘zalligini kashf etasizmi — BlackRide Experience har bir safarni qulaylik, maxfiylik va silliq muvofiqlashtirish atrofida quradi.",
      button: "Tajribamni rejalashtirish",
    },
    stickyCta: "Tajribamni rejalashtirish",
  },
  ru: {
    home: "Главная",
    experience: "BlackRide Experience",
    languageLabel: "Язык страницы",
    trust: {
      kicker: "Выбор взыскательных путешественников",
      body: "Создано для гостей, которые ценят приватность, ясность и безупречно организованное передвижение.",
    },
    rating: { title: "4.9/5 качество сервиса", subtitle: "Настраиваемый знак доверия" },
    destinationIntro: {
      eyebrow: "Исследуйте направления",
      title: "Путешествия по направлениям, выстроенные под реальный ритм premium-гостей",
      body: "BlackRide Experience задуман не как каталог туров, а как продуманный консьерж-планировщик. Каждое путешествие строится вокруг гостя, логики маршрута, аэропортовой координации и премиального наземного движения.",
    },
    whyIntro: {
      eyebrow: "Почему стоит путешествовать с BlackRide Experience?",
      title: "Более осмысленный стандарт путешествий по направлениям",
      body: "Для гостей, которые ценят приватность, тайминг и высокий уровень координации, разница чувствуется в деталях: в тоне встречи, выборе автомобиля, переходах между городами и качестве сопровождения.",
    },
    featuredIntro: {
      eyebrow: "Избранные впечатления",
      title: "Маршруты, созданные под цель, а не по шаблону",
      body: "Кто-то приезжает ради Умры, кто-то — ради частного отдыха, деловой программы или сочетания нескольких задач. Эти форматы показывают, какие направления и сценарии BlackRide может курировать.",
    },
    saudiIntro: {
      eyebrow: "Опыт Саудовской Аравии",
      title: "Премиальное путешествие для Умры и городов",
      body: "Спокойный 6-дневный маршрут по Джидде, Мекке и Медине с chauffeur-сопровождением на каждом этапе. Формат создан для гостей, которым нужен более собранный саудовский маршрут с приватными междугородними переездами и понятной логистикой.",
    },
    saudiCta: {
      kicker: "Частное планирование по Саудии",
      title: "От прилёта до финального выезда каждое перемещение ощущается более уверенно.",
      body: "BlackRide поддерживает саудовские маршруты через премиальные встречи в аэропорту, подбор семейных автомобилей, координацию междугородних переездов и более спокойный ритм между Меккой, Мединой и Джиддой.",
      primary: "Запланировать мой маршрут по Саудии",
      secondary: "Смотреть полный опыт по Саудии",
    },
    uzbekIntro: {
      eyebrow: "Опыт Узбекистана",
      title: "Премиальный семейный маршрут по Узбекистану 5 дней / 5 ночей",
      body: "Ташкент • Самарканд • Бухара • Горы и озеро. Этот маршрут разработан и проводится HorizonCrew Uzbekistan с наземной координацией и сопровождением на протяжении всей поездки.",
      note: "Даты подтверждаются отдельно. Маршрут может быть адаптирован под перелёты, размер группы, категорию отеля и пожелания гостей.",
      kicker: "Проводится HorizonCrew Uzbekistan",
      ctaTitle: "Культурное путешествие с уже выстроенным местным ритмом.",
      ctaBody: "От прибытия в Ташкент до горных дней и городов Шёлкового пути — опыт Узбекистана представлен через BlackRide как премиально курируемый вариант и реализуется на месте HorizonCrew Uzbekistan.",
      primary: "Запланировать мой маршрут по Узбекистану",
      secondary: "Запросить индивидуальные даты",
    },
    fleetIntro: {
      eyebrow: "Путешествуйте в комфорте и стиле",
      title: "Автопарк, сформированный вокруг гостя, а не наоборот",
      body: "Автомобиль имеет значение, потому что именно он меняет ощущение дня. Семьям нужна вместительность, руководителям — приватность, прибытиям — спокойствие, а междугородним поездкам — правильный баланс пространства и сервиса.",
    },
    differenceIntro: {
      eyebrow: "Преимущество BlackRide Experience",
      title: "Luxury-путешествие становится легче, когда вопрос передвижения уже решён",
      body: "Большая часть трения в путешествии возникает между ключевыми моментами: встреча, трансфер, багаж, неподходящий автомобиль, потерянный тайминг, фрагментированная поддержка. BlackRide Experience создан, чтобы элегантно закрывать эти пробелы.",
      standard: "Обычное путешествие",
      elevated: "BlackRide Experience",
    },
    howItWorks: {
      eyebrow: "Как это работает",
      title: "Пять спокойных шагов от первого запроса до финального прибытия",
      body: "Процесс планирования намеренно остаётся простым, чтобы путешествие можно было сделать более персональным без лишней сложности для гостя.",
    },
    enquire: {
      eyebrow: "Давайте спланируем ваше следующее путешествие",
      title: "Поделитесь замыслом поездки — мы выстроим впечатление вокруг него.",
      body: "Расскажите, куда вы направляетесь, сколько гостей будет в поездке и какой формат впечатления вы хотите. Мы используем это как основу для следующего шага планирования.",
      bullets: [
        "Подходит для премиальных семейных поездок, деловых графиков, Умры и частных персонализированных маршрутов.",
        "Финальный маршрут, включения, категория отеля, наличие гида, активности, класс поезда, входные билеты, питание, стоимость и даты подтверждаются на этапе запроса.",
        "Перелёты, визы и отели не следует считать включёнными, если это отдельно не подтверждено в финальном плане.",
      ],
      whatsapp: "WhatsApp concierge",
      email: "Отправить запрос по email",
      fullName: "Полное имя",
      whatsappNumber: "Номер WhatsApp",
      emailLabel: "Email",
      destination: "Направление",
      travelMonth: "Месяц поездки",
      guests: "Количество гостей",
      experienceType: "Предпочтительный формат",
      requirements: "Сообщение / пожелания",
      travelMonthPlaceholder: "Например: сентябрь 2026",
      requirementsPlaceholder: "Укажите маршрут, профиль гостей, предпочтения по отелям, деловой график или особые требования.",
      submit: "Запросить curated experience",
      chat: "Написать в WhatsApp",
    },
    faqIntro: {
      eyebrow: "Часто задаваемые вопросы",
      title: "Несколько полезных деталей перед запросом",
      body: "Итоговая конфигурация поездки всегда подтверждается вокруг потребностей гостя, но эти ответы охватывают самые частые вопросы.",
    },
    relatedIntro: {
      eyebrow: "Связанные сервисы BlackRide",
      title: "Продолжите планирование саудовской части поездки",
      body: "Если ваш маршрут включает Саудовскую Аравию, эти страницы помогут глубже понять логику маршрутов, аэропортовых перемещений и chauffeur-сервиса.",
    },
    finalCta: {
      title: "Ваше путешествие должно ощущаться лёгким с самой первой минуты после прилёта.",
      body: "Путешествуете ли вы в Саудовскую Аравию ради Умры, бизнеса или отдыха — или открываете для себя вечную красоту Узбекистана — BlackRide Experience выстраивает каждую поездку вокруг комфорта, приватности и безупречной координации.",
      button: "Запланировать моё путешествие",
    },
    stickyCta: "Запланировать путешествие",
  },
};

const HERO_TRANSLATIONS = {
  ar: [
    {
      eyebrow: "تجارب سفر فاخرة",
      title: "رحلات تبقى معك، حتى بعد العودة.",
      description: "منسقة بعناية. مدفوعة بالراحة. ومصممة لمن يتوقعون أكثر.",
      destinationLine: "السعودية. أوزبكستان. ذكريات لا تُنسى.",
      primaryCta: "استكشف السعودية",
      secondaryCta: "استكشف أوزبكستان",
      previewTitle: "السعودية",
      previewSubtitle: "روائع خالدة. وراحة عصرية.",
      previewDescription: "اكتشف الروحانية والتراث والفخامة عبر أبرز الوجهات.",
    },
    {
      eyebrow: "تجربة عمرة فاخرة",
      title: "رحلة روحانية، خُططت بعناية.",
      description: "من الوصول إلى المطار وحتى مكة والمدينة وكل محطة مهمة بينهما، سافر براحة وخصوصية وطمأنينة.",
      destinationLine: "عمرة. مكة. المدينة. بسلاسة.",
      primaryCta: "استكشف رحلة العمرة",
      secondaryCta: "خطط تجربتي",
      previewTitle: "رحلة العمرة",
      previewSubtitle: "روحانية. هدوء. سلاسة.",
      previewDescription: "رحلة روحانية تجمع الراحة والخصوصية وراحة البال.",
    },
    {
      eyebrow: "تجارب طريق الحرير",
      title: "تراث خالد، منسق للمسافر العصري.",
      description: "استكشف سمرقند وبخارى وطشقند عبر رحلات ثقافية موجهة وتنسيق خاص وراحة سفر راقية.",
      destinationLine: "أوزبكستان. طريق الحرير. الثقافة.",
      primaryCta: "استكشف أوزبكستان",
      secondaryCta: "خطط رحلتي",
      previewTitle: "أوزبكستان",
      previewSubtitle: "تراث طريق الحرير. جمال خالد.",
      previewDescription: "اكتشف المدن العتيقة والثقافة النابضة والمناظر المدهشة.",
    },
    {
      eyebrow: "رحلات الطبيعة والعائلة",
      title: "حيث يصبح السكون رفاهية بحد ذاته.",
      description: "من هواء الجبال إلى صفاء البحيرات، اكتشف جمال أوزبكستان الطبيعي عبر رحلة مصممة وفق إيقاعك.",
      destinationLine: "جبال. بحيرات. مغامرة. سكينة.",
      primaryCta: "استكشف الطبيعة",
      secondaryCta: "خطط تجربتي",
      previewTitle: "الجبال والبحيرات",
      previewSubtitle: "ملاذ طبيعي. صفاء خالص.",
      previewDescription: "من القمم المكسوة بالثلوج إلى البحيرات الشفافة، عِش الطبيعة بشكل مختلف.",
    },
    {
      eyebrow: "تجارب السفر التنفيذي",
      title: "سفر الأعمال، بمستوى يتجاوز الجدول.",
      description: "للتنفيذيين والوفود والضيوف المهمين، تجمع بلاك رايد إكسبيرينس بين النقل الراقي والتنسيق المحلي والدعم المتحفظ.",
      destinationLine: "الرياض. جدة. الدمام. جاهزية تنفيذية.",
      primaryCta: "استكشف سفر الأعمال",
      secondaryCta: "تواصل مع الكونسيرج",
      previewTitle: "السفر التنفيذي",
      previewSubtitle: "خصوصية. كفاءة. فخامة.",
      previewDescription: "رحلات الأعمال بدعم نقل فاخر وتنسيق كونسيرج أنيق.",
    },
  ],
  uz: [
    {
      eyebrow: "Premium sayohat tajribalari",
      title: "Safardan qaytgach ham siz bilan qoladigan yo‘llar.",
      description: "E’tibor bilan kuratsiya qilingan. Qulaylik bilan boshqarilgan. Ko‘proq narsani kutadiganlar uchun yaratilgan.",
      destinationLine: "Saudiya Arabistoni. O‘zbekiston. Unutilmas.",
      primaryCta: "Saudiyani kashf eting",
      secondaryCta: "O‘zbekistonni kashf eting",
      previewTitle: "Saudiya Arabistoni",
      previewSubtitle: "Abadiy mo‘jizalar. Zamonaviy qulaylik.",
      previewDescription: "Ruhiyat, meros va hashamatni mashhur manzillar bo‘ylab his eting.",
    },
    {
      eyebrow: "Premium Umra tajribasi",
      title: "Ma’naviy safar, e’tibor bilan rejalashtirilgan.",
      description: "Aeroportdan kutib olishdan tortib Makkah, Madina va barcha muhim manzillargacha qulaylik, maxfiylik va xotirjamlik bilan sayohat qiling.",
      destinationLine: "Umra. Makkah. Madina. Silliq harakat.",
      primaryCta: "Umra safarini ko‘rish",
      secondaryCta: "Tajribamni rejalashtirish",
      previewTitle: "Umra safri",
      previewSubtitle: "Ma’naviy. Tinch. Silliq.",
      previewDescription: "Qulaylik, maxfiylik va xotirjamlik bilan kechadigan ruhiy safar.",
    },
    {
      eyebrow: "Ipak yo‘li tajribalari",
      title: "Zamonaviy sayohatchi uchun kuratsiya qilingan abadiy meros.",
      description: "Samarqand, Buxoro va Toshkentni gidli madaniy safarlar, xususiy koordinatsiya va premium sayohat qulayligi bilan kashf eting.",
      destinationLine: "O‘zbekiston. Ipak yo‘li. Madaniyat.",
      primaryCta: "O‘zbekistonni kashf eting",
      secondaryCta: "Safarimni rejalashtirish",
      previewTitle: "O‘zbekiston",
      previewSubtitle: "Ipak yo‘li merosi. Abadiy go‘zallik.",
      previewDescription: "Qadimiy shaharlar, yorqin madaniyat va go‘zal manzaralarni kashf eting.",
    },
    {
      eyebrow: "Tabiat va oilaviy dam olish",
      title: "Sokinlikning o‘zi hashamatga aylanadigan joy.",
      description: "Tog‘ havosi va tiniq ko‘l manzaralaridan bahramand bo‘ling, safar esa aynan sizning ritmingiz atrofida qurilsin.",
      destinationLine: "Tog‘lar. Ko‘llar. Sarguzasht. Sokilik.",
      primaryCta: "Tabiat safarini ko‘rish",
      secondaryCta: "Tajribamni rejalashtirish",
      previewTitle: "Tog‘lar va ko‘llar",
      previewSubtitle: "Tabiat bag‘ri. Sof xotirjamlik.",
      previewDescription: "Qorli cho‘qqilar va shaffof ko‘llar oralig‘ida tabiatni yangicha his eting.",
    },
    {
      eyebrow: "Ijrochi sayohat tajribalari",
      title: "Biznes safar, reja doirasidan yuqori darajada.",
      description: "Rahbarlar, delegatsiyalar va VIP mehmonlar uchun BlackRide Experience premium transport, mahalliy koordinatsiya va ehtiyotkor yordamni birlashtiradi.",
      destinationLine: "Riyod. Jidda. Dammam. Tayyor.",
      primaryCta: "Biznes safarini ko‘rish",
      secondaryCta: "Concierge bilan bog‘lanish",
      previewTitle: "Biznes safar",
      previewSubtitle: "Maxfiy. Tezkor. Premium.",
      previewDescription: "Premium transport va concierge koordinatsiyasi bilan qo‘llab-quvvatlanadigan korporativ safarlar.",
    },
  ],
  ru: [
    {
      eyebrow: "Премиальные travel experiences",
      title: "Путешествия, которые остаются с вами и после возвращения.",
      description: "С заботой продумано. Построено вокруг комфорта. Создано для тех, кто ожидает большего.",
      destinationLine: "Саудовская Аравия. Узбекистан. Незабываемо.",
      primaryCta: "Открыть Саудию",
      secondaryCta: "Открыть Узбекистан",
      previewTitle: "Саудовская Аравия",
      previewSubtitle: "Вечные чудеса. Современный комфорт.",
      previewDescription: "Откройте духовность, наследие и роскошь через знаковые направления.",
    },
    {
      eyebrow: "Премиальный опыт Умры",
      title: "Духовное путешествие, спланированное с заботой.",
      description: "От встречи в аэропорту до Мекки, Медины и каждой значимой остановки между ними — путешествуйте в комфорте, приватности и спокойствии.",
      destinationLine: "Умра. Мекка. Медина. Безупречно.",
      primaryCta: "Открыть маршрут Умры",
      secondaryCta: "Запланировать мой опыт",
      previewTitle: "Путешествие Умры",
      previewSubtitle: "Духовно. Спокойно. Безупречно.",
      previewDescription: "Духовный маршрут с комфортом, приватностью и спокойствием.",
    },
    {
      eyebrow: "Впечатления Шёлкового пути",
      title: "Вечное наследие, curated для современного путешественника.",
      description: "Исследуйте Самарканд, Бухару и Ташкент через культурные маршруты с гидом, частную координацию и премиальный комфорт.",
      destinationLine: "Узбекистан. Шёлковый путь. Культура.",
      primaryCta: "Открыть Узбекистан",
      secondaryCta: "Запланировать маршрут",
      previewTitle: "Узбекистан",
      previewSubtitle: "Наследие Шёлкового пути. Вечная красота.",
      previewDescription: "Откройте древние города, яркую культуру и впечатляющие пейзажи.",
    },
    {
      eyebrow: "Природа и семейные маршруты",
      title: "Где тишина сама становится роскошью.",
      description: "От горного воздуха до кристальных озёр — откройте природную сторону Узбекистана в ритме, созданном вокруг вас.",
      destinationLine: "Горы. Озёра. Приключение. Спокойствие.",
      primaryCta: "Открыть природный маршрут",
      secondaryCta: "Запланировать мой опыт",
      previewTitle: "Горы и озёра",
      previewSubtitle: "Побег к природе. Чистое спокойствие.",
      previewDescription: "От снежных вершин до прозрачных озёр — увидьте природу по-новому.",
    },
    {
      eyebrow: "Executive travel experiences",
      title: "Деловые поездки, выведенные за пределы расписания.",
      description: "Для руководителей, делегаций и VIP-гостей BlackRide Experience объединяет премиальный транспорт, локальную координацию и деликатную поддержку.",
      destinationLine: "Эр-Рияд. Джидда. Даммам. Готово к приёму.",
      primaryCta: "Открыть business travel",
      secondaryCta: "Связаться с concierge",
      previewTitle: "Деловые поездки",
      previewSubtitle: "Деликатно. Эффективно. Премиально.",
      previewDescription: "Корпоративные маршруты с премиальным транспортом и concierge-координацией.",
    },
  ],
};

const TRUST_POINT_TRANSLATIONS = {
  ar: [
    "أسطول فاخر مع سائق",
    "استقبال في المطار",
    "برامج منسقة",
    "مساعدة إرشادية",
    "كونسيرج واتساب 24/7",
    "شركاء محليون موثوقون",
  ],
  uz: [
    "Premium chauffeur floti",
    "Aeroportda kutib olish",
    "Kuratsiyalangan marshrutlar",
    "Yo‘l-yo‘riq yordami",
    "24/7 WhatsApp concierge",
    "Ishonchli mahalliy hamkorlar",
  ],
  ru: [
    "Премиальный chauffeur-флот",
    "Встреча в аэропорту",
    "Curated-маршруты",
    "Продуманное сопровождение",
    "24/7 WhatsApp concierge",
    "Проверенные локальные партнёры",
  ],
};

const DESTINATION_CARD_TRANSLATIONS = {
  ar: [
    {
      eyebrow: "السعودية",
      title: "تجربة عمرة ومدينة فاخرة",
      subtitle: "عمرة • تراث • ثقافة • أعمال",
      description:
        "رحلات روحانية، ومدن تاريخية، وفخامة عصرية، وتنقل خاص مع سائق عبر جدة ومكة والمدينة والرياض وما بعدها.",
      badges: ["6 أيام", "سائق خاص", "مناسب للعائلة"],
      cta: "استكشف التجربة السعودية",
    },
    {
      eyebrow: "أوزبكستان",
      title: "رحلة طريق الحرير والتراث",
      subtitle: "طريق الحرير • جبال • ثقافة • طعام",
      description:
        "مدن خالدة، ومناظر مدهشة، ومطبخ غني، وتجارب ثقافية موجهة عبر طشقند وسمرقند وبخارى وأميرسوي وشارفاك.",
      badges: ["5 أيام", "جولات موجهة", "مناسب للعائلة"],
      cta: "استكشف تجربة أوزبكستان",
    },
  ],
  uz: [
    {
      eyebrow: "Saudiya Arabistoni",
      title: "Premium Umra va shahar tajribasi",
      subtitle: "Umra • Meros • Madaniyat • Biznes",
      description:
        "Jidda, Makkah, Madina, Riyod va undan keyingi yo‘nalishlar bo‘ylab ruhiy safar, tarixiy shaharlar va xususiy chauffeur-led harakat.",
      badges: ["6 kun", "Xususiy chauffeur", "Oilaviy"],
      cta: "Saudiya tajribasini ko‘rish",
    },
    {
      eyebrow: "O‘zbekiston",
      title: "Ipak yo‘li va meros safri",
      subtitle: "Ipak yo‘li • Tog‘lar • Madaniyat • Taomlar",
      description:
        "Toshkent, Samarqand, Buxoro, Amirsoy va Chorvoq bo‘ylab vaqt sinovidan o‘tgan shaharlar, go‘zal manzaralar va gidli madaniy tajribalar.",
      badges: ["5 kun", "Gidli safar", "Oilaviy"],
      cta: "O‘zbekiston tajribasini ko‘rish",
    },
  ],
  ru: [
    {
      eyebrow: "Саудовская Аравия",
      title: "Премиальный опыт Умры и городов",
      subtitle: "Умра • Наследие • Культура • Бизнес",
      description:
        "Духовные маршруты, исторические города, современная роскошь и private chauffeur travel по Джидде, Мекке, Медине, Эр-Рияду и далее.",
      badges: ["6 дней", "Private chauffeur", "Для семьи"],
      cta: "Открыть опыт Саудии",
    },
    {
      eyebrow: "Узбекистан",
      title: "Путешествие по Шёлковому пути и наследию",
      subtitle: "Шёлковый путь • Горы • Культура • Кухня",
      description:
        "Вечные города, впечатляющие пейзажи, богатая кухня и guided cultural experiences по Ташкенту, Самарканду, Бухаре, Amirsoy и Чарваку.",
      badges: ["5 дней", "Guided tours", "Для семьи"],
      cta: "Открыть опыт Узбекистана",
    },
  ],
};

const WHY_CARD_TRANSLATIONS = {
  ar: [
    {
      title: "أسطول فاخر",
      description: "سيارات تنفيذية، وSUV مناسبة للعائلة، وفانات مريحة، ومركبات أكبر تُختار بحسب الضيف والأمتعة وطبيعة المسار.",
    },
    {
      title: "استقبال في المطار",
      description: "وصول أكثر هدوءاً مع خدمة الاستقبال والمساعدة في الأمتعة وانتقال أكثر سلاسة من الصالة إلى المركبة.",
    },
    {
      title: "تجارب منسقة",
      description: "برامج تقودها الوجهة ومصممة حول المسافر، لا حول جدول جماعي سريع أو باقة عامة.",
    },
    {
      title: "خبرة محلية",
      description: "تنسيق محلي موثوق، وتخطيط عملي للمسارات، ودعم وجهة يجعل التنقل بين المدن أكثر أناقة وقابلية للتنبؤ.",
    },
    {
      title: "دعم 24/7",
      description: "تنسيق عبر واتساب لتحديثات الوصول، وتعديلات البرنامج، وتخطيط المركبات، والتفاصيل الصغيرة التي تهم في الرحلات الراقية.",
    },
    {
      title: "مناسب للعائلة والتنفيذيين",
      description: "مصمم لرجال الأعمال والضيوف المستضافين وعائلات الخليج والمسافرين الذين يقدّرون الخصوصية والتوقيت وسلاسة الحركة.",
    },
  ],
  uz: [
    {
      title: "Premium avtopark",
      description: "Ijrochi sedanlar, oilaga mos SUVlar, qulay vanlar va katta avtomobillar mehmon profili, yuk va yo‘nalishga qarab tanlanadi.",
    },
    {
      title: "Aeroportda kutib olish",
      description: "Terminaldan avtomobilgacha xotirjam va tartibli o‘tish uchun kutib olish, yuk yordami va sokin transfer.",
    },
    {
      title: "Kuratsiyalangan tajribalar",
      description: "Shablon paket emas, mehmon va yo‘nalish ritmiga moslashtirilgan destination-led marshrutlar.",
    },
    {
      title: "Mahalliy ekspertiza",
      description: "Ishonchli mahalliy koordinatsiya, amaliy yo‘l rejalari va shaharlar oralig‘idagi harakatni silliq qiladigan yordam.",
    },
    {
      title: "24/7 yordam",
      description: "Kelish yangilanishlari, marshrut o‘zgarishlari, avtomobil rejalari va muhim mayda tafsilotlar uchun WhatsApp orqali yordam.",
    },
    {
      title: "Oila va rahbarlar учун mos",
      description: "Biznes egalari, hosted mehmonlar, GCC oilalari va maxfiylik hamda vaqtni qadrlaydigan sayohatchilar uchun mos.",
    },
  ],
  ru: [
    {
      title: "Премиальный автопарк",
      description: "Исполнительские седаны, семейные SUV, комфортные вены и более крупные автомобили подбираются под гостя, багаж и формат маршрута.",
    },
    {
      title: "Встреча в аэропорту",
      description: "Более спокойное прибытие с meet-and-greet, помощью с багажом и мягкой передачей от терминала к автомобилю.",
    },
    {
      title: "Curated experiences",
      description: "Маршруты выстраиваются вокруг гостя и направления, а не вокруг стандартного пакетного шаблона.",
    },
    {
      title: "Локальная экспертиза",
      description: "Проверенная локальная координация, практичная логика маршрута и поддержка, делающая переходы между городами более предсказуемыми.",
    },
    {
      title: "Поддержка 24/7",
      description: "WhatsApp-координация для обновлений по прилёту, корректировок маршрута, подбора автомобиля и важных мелочей премиальной поездки.",
    },
    {
      title: "Для семьи и руководителей",
      description: "Подходит для владельцев бизнеса, hosted-гостей, семей GCC и путешественников, которые ценят приватность, тайминг и плавное перемещение.",
    },
  ],
};

const FEATURED_TRANSLATIONS = {
  ar: [
    { tag: "الأكثر طلباً", title: "رحلة عمرة فاخرة", description: "رحلة سعودية منسقة تربط الوصول بالمطار، ومكة، والمدينة، والتنقلات الزيارية الخاصة، وحركة بين المدن مصقولة.", cta: "عرض الرحلة السعودية" },
    { tag: "مفضلة للعائلة", title: "الهروب إلى طريق الحرير", description: "برنامج غني الإيقاع عبر طشقند وسمرقند وبخارى والجبال والبحيرات مع مساعدة محلية موجهة.", cta: "عرض رحلة أوزبكستان" },
    { tag: "أعمال", title: "رحلة أعمال تنفيذية في السعودية", description: "صيغة سفر مصقولة للمديرين والضيوف المهمين الذين يحتاجون إلى تنقل سلس بين المطار والفندق والاجتماعات.", cta: "ناقش سفر الأعمال" },
    { tag: "مخصص", title: "رحلة خاصة مخصصة", description: "تجربة خاصة تُبنى حول مدنك وتوقيتك وطبيعة العائلة وتفضيل الفندق والنبرة التي تريدها للرحلة.", cta: "اطلب خطة منسقة" },
  ],
  uz: [
    { tag: "Mashhur", title: "Premium Umra safri", description: "Aeroportdan kutib olish, Makkah, Madina va silliq shaharlararo harakatni bog‘laydigan Saudiya marshruti.", cta: "Saudiya safarini ko‘rish" },
    { tag: "Oilaviy sevimli", title: "Ipak yo‘li qochishi", description: "Toshkent, Samarqand, Buxoro, tog‘lar va ko‘llar bo‘ylab boy ritmli yo‘nalish va mahalliy yordam.", cta: "O‘zbekiston safarini ko‘rish" },
    { tag: "Biznes", title: "Ijrochi Saudiya biznes safari", description: "Aeroport, mehmonxona va uchrashuvlar o‘rtasida silliq harakat kerak bo‘lgan premium mehmonlar uchun puxta format.", cta: "Biznes safarini muhokama qilish" },
    { tag: "Moslashtirilgan", title: "Xususiy mos safar", description: "Shaharlar, vaqt, oila tarkibi, mehmonxona talabi va umumiy kayfiyat atrofida qurilgan xususiy tajriba.", cta: "Kuratsiyalangan reja so‘rash" },
  ],
  ru: [
    { tag: "Популярно", title: "Премиальное путешествие Умры", description: "Курируемый саудовский маршрут, соединяющий прилёт, Мекку, Медину и деликатно выстроенные междугородние переезды.", cta: "Смотреть маршрут по Саудии" },
    { tag: "Любимо семьями", title: "Silk Road Escape", description: "Насыщенный по темпу маршрут через Ташкент, Самарканд, Бухару, горы и озёра с локальным сопровождением.", cta: "Смотреть маршрут по Узбекистану" },
    { tag: "Бизнес", title: "Исполнительский маршрут по Саудии", description: "Выверенный деловой travel-формат для руководителей и hosted-гостей, которым нужна бесшовная логистика.", cta: "Обсудить business travel" },
    { tag: "Индивидуально", title: "Индивидуальное частное путешествие", description: "Частный формат, выстроенный вокруг ваших городов, тайминга, состава семьи и желаемого тона поездки.", cta: "Запросить curated-план" },
  ],
};

const SAUDI_TIMELINE_TRANSLATIONS = {
  ar: [
    { day: "اليوم 1", title: "الوصول إلى جدة والانتقال إلى مكة", items: ["استقبال في المطار", "مساعدة في الأمتعة", "مركبة بلاك رايد فاخرة", "نقل خاص من مطار جدة إلى مكة"] },
    { day: "اليوم 2", title: "زيارات مكة والتنقل المحلي", items: ["دعم سائق خاص داخل مكة", "مواقع الزيارة حسب الوصول والتوقيت المحلي", "تنقل عائلي مرن"] },
    { day: "اليوم 3", title: "الانتقال من مكة إلى المدينة", items: ["نقل خاص بين المدن", "مركبة واسعة للعائلة والأمتعة", "رحلة مريحة ومجدولة"] },
    { day: "اليوم 4", title: "زيارات المدينة", items: ["تنسيق زيارات محلية عند الترتيب", "تنقل بقيادة سائق", "معالم مهمة حسب الوصول"] },
    { day: "اليوم 5", title: "الانتقال من المدينة إلى جدة", items: ["نقل خاص إلى جدة", "توقفات راحة عند الحاجة", "مساندة الوصول إلى الفندق أو المدينة"] },
    { day: "اليوم 6", title: "تجربة جدة والمغادرة", items: ["البُلد أو الواجهة البحرية أو التسوق أو الطعام حسب الرغبة", "توقيت مدينة مرن", "نقل نهائي إلى المطار"] },
  ],
  uz: [
    { day: "1-kun", title: "Jiddaga kelish va Makkahga transfer", items: ["Aeroportda kutib olish", "Yuk bilan yordam", "Premium BlackRide avtomobili", "Jidda aeroportidan Makkahga xususiy transfer"] },
    { day: "2-kun", title: "Makkah ziyorati va mahalliy harakat", items: ["Makkah ichida chauffeur yordami", "Ziyorat joylari kirish va vaqtga qarab", "Moslashuvchan oilaviy harakat"] },
    { day: "3-kun", title: "Makkahdan Madinaga transfer", items: ["Xususiy shaharlararo transfer", "Oila va yuk uchun keng avtomobil", "Qulay rejalashtirilgan safar"] },
    { day: "4-kun", title: "Madina ziyorati", items: ["Kerak bo‘lsa mahalliy ziyorat koordinatsiyasi", "Chauffeur-led harakat", "Muhim maskanlar kirish imkoniga qarab"] },
    { day: "5-kun", title: "Madinadan Jiddaga transfer", items: ["Jiddaga xususiy transfer", "Zaruratga qarab qulay to‘xtashlar", "Mehmonxona yoki shahar kirishida yordam"] },
    { day: "6-kun", title: "Jidda tajribasi va jo‘nash", items: ["Al Balad, Waterfront, shopping yoki dining ixtiyoriy", "Moslashuvchan shahar vaqti", "Yakuniy aeroport transferi"] },
  ],
  ru: [
    { day: "День 1", title: "Прилёт в Джидду и трансфер в Мекку", items: ["Встреча в аэропорту", "Помощь с багажом", "Премиальный автомобиль BlackRide", "Частный трансфер из аэропорта Джидды в Мекку"] },
    { day: "День 2", title: "Зиярат в Мекке и локальные перемещения", items: ["Поддержка chauffeur в Мекке", "Точки зиярата в зависимости от доступа и тайминга", "Гибкое семейное перемещение"] },
    { day: "День 3", title: "Трансфер из Мекки в Медину", items: ["Частный междугородний трансфер", "Просторный автомобиль для семьи и багажа", "Комфортный запланированный переезд"] },
    { day: "День 4", title: "Зиярат в Медине", items: ["Локальная координация зиярата при необходимости", "Перемещения с chauffeur", "Ключевые места в зависимости от доступа"] },
    { day: "День 5", title: "Трансфер из Медины в Джидду", items: ["Частный трансфер в Джидду", "Комфортные остановки при необходимости", "Поддержка при прибытии в отель или город"] },
    { day: "День 6", title: "Опыт Джидды и вылет", items: ["Al Balad, Waterfront, shopping или dining по желанию", "Гибкий городской тайминг", "Финальный трансфер в аэропорт"] },
  ],
};

const UZBEKISTAN_TIMELINE_TRANSLATIONS = {
  ar: [
    { day: "اليوم 1", title: "الوصول إلى طشقند وتجربة المدينة المسائية", items: ["استقبال في المطار", "نقل إلى الفندق", "غداء أوزبكي تقليدي", "Tashkent City Mall وPark", "متحف الشمع", "عشاء خفيف أو محطة طعام محلية"] },
    { day: "اليوم 2", title: "أميرسوي وتشيمغان وبحيرة شارفاك", items: ["منتجع أميرسوي الثلجي", "تلفريك بانورامي", "أنشطة جبلية", "إطلالات بحيرة شارفاك", "Ice City أو تجربة Seoul Mun المسائية"] },
    { day: "اليوم 3", title: "جولة سمرقند التراثية", items: ["قطار من طشقند إلى سمرقند", "ضريح كور أمير", "ساحة ريجستان", "مسجد بيبي خانوم", "سوق سياب", "شاه زنده", "Silk Road Samarkand", "الانتقال إلى بخارى"] },
    { day: "اليوم 4", title: "تجربة بخارى التاريخية", items: ["مسجد بولو حوض", "ستاره ماهي خاصة", "مجمع بهاء الدين نقشبند", "بخارى القديمة", "ليابي حوض", "مئذنة كلان", "عشاء مشاوي مجاني من HorizonCrew", "قطار ليلي إلى طشقند"] },
    { day: "اليوم 5", title: "جولة طشقند والمغادرة", items: ["مجمع حضرة الإمام", "سوق تشورسو", "مسجد Minor White", "Central Park", "برج طشقند التلفزيوني اختياري", "Magic City", "نقل إلى المطار"] },
  ],
  uz: [
    { day: "1-kun", title: "Toshkentga kelish va kechki shahar tajribasi", items: ["Aeroportda kutib olish", "Mehmonxonaga transfer", "An’anaviy o‘zbek tushligi", "Tashkent City Mall va Park", "Wax Museum", "Yengil kechki ovqat yoki mahalliy food stop"] },
    { day: "2-kun", title: "Amirsoy, Chimgan va Chorvoq ko‘li", items: ["Amirsoy Snow Resort", "Manzarali kanat yo‘li", "Tog‘ faoliyatlari", "Chorvoq ko‘li manzaralari", "Ice City yoki Seoul Mun kechki tajribasi"] },
    { day: "3-kun", title: "Samarqand meros safari", items: ["Toshkentdan Samarqandga poyezd", "Go‘ri Amir maqbarasi", "Registon maydoni", "Bibi Xonim masjidi", "Siyob bozori", "Shohi Zinda", "Silk Road Samarkand", "Buxoroga transfer"] },
    { day: "4-kun", title: "Buxoro tarixiy tajribasi", items: ["Bolo Hovuz masjidi", "Sitorai Mohi Xosa", "Bahouddin Naqshband majmuasi", "Eski Buxoro", "Labi Hovuz", "Kalon minorasi", "HorizonCrew’dan complimentary grill dinner", "Toshkentga kechki poyezd"] },
    { day: "5-kun", title: "Toshkent city tour va jo‘nash", items: ["Hazrati Imom majmuasi", "Chorsu bozori", "Minor oq masjidi", "Central Park", "Toshkent teleminorasi ixtiyoriy", "Magic City", "Aeroport transferi"] },
  ],
  ru: [
    { day: "День 1", title: "Прибытие в Ташкент и вечерний городской опыт", items: ["Встреча в аэропорту", "Трансфер в отель", "Традиционный узбекский обед", "Tashkent City Mall и Park", "Wax Museum", "Лёгкий ужин или local food stop"] },
    { day: "День 2", title: "Amirsoy, Чимган и озеро Чарвак", items: ["Amirsoy Snow Resort", "Панорамная канатная дорога", "Горные активности", "Виды на озеро Чарвак", "Ice City или вечерний Seoul Mun"] },
    { day: "День 3", title: "Наследие Самарканда", items: ["Поезд из Ташкента в Самарканд", "Мавзолей Гур-Эмир", "Площадь Регистан", "Мечеть Биби-Ханым", "Сиабский базар", "Шахи-Зинда", "Silk Road Samarkand", "Трансфер в Бухару"] },
    { day: "День 4", title: "Исторический опыт Бухары", items: ["Мечеть Боло Хауз", "Ситораи Мохи Хоса", "Комплекс Бахауддина Накшбанда", "Старая Бухара", "Ляби-Хауз", "Минарет Калян", "Комплиментарный grilled dinner от HorizonCrew", "Ночной поезд в Ташкент"] },
    { day: "День 5", title: "Тур по Ташкенту и вылет", items: ["Комплекс Хазрати Имам", "Базар Чорсу", "Белая мечеть Minor", "Central Park", "Ташкентская телебашня по желанию", "Magic City", "Трансфер в аэропорт"] },
  ],
};

const FLEET_TRANSLATIONS = {
  ar: [
    { useCase: "سفر تنفيذي", note: "سيدان رائدة للوصولات المستضافة، وتنقلات القيادات العليا، والحركة الراقية داخل المدينة." },
    { useCase: "مجموعات VIP", note: "خيار قوي لوصولات العائلات، والتنقلات الفاخرة من المطار، والضيوف ذوي الأمتعة والخصوصية الأكبر." },
    { useCase: "سفر عائلي", note: "تنقل مريح للعائلات والمجموعات الخاصة الصغيرة التي تفضل مساحة هادئة." },
    { useCase: "راحة فاخرة", note: "خيار عصري مصقول للضيوف الذين يريدون مساحة أكبر دون الانتقال إلى مركبة جماعية أكبر." },
    { useCase: "سفر جماعي", note: "مفيد للوفود، والعائلات الكبيرة، والتنقلات المجدولة مع سعة إضافية للأمتعة." },
    { useCase: "سفر أعمال", note: "راحة تنفيذية هادئة لانتقالات الفنادق والاجتماعات المستضافة والجداول الحضرية الهادئة." },
  ],
  uz: [
    { useCase: "Ijrochi safar", note: "Hosted arrivals, yuqori rahbariyat harakati va nafis shahar yo‘nalishlari uchun flagman sedan." },
    { useCase: "VIP guruhlar", note: "Oilaviy kelishlar, premium aeroport yo‘nalishlari va ko‘proq yuk hamda maxfiylik talablari uchun kuchli moslik." },
    { useCase: "Oilaviy safar", note: "Oila va kichik xususiy guruhlar uchun sokin va qulay van safari." },
    { useCase: "Premium qulaylik", note: "Kattaroq group vehicle’ga o‘tmasdan ko‘proq joy istaydigan mehmonlar uchun zamonaviy variant." },
    { useCase: "Guruh safri", note: "Delegatsiyalar, katta oilalar va qo‘shimcha yuk sig‘imi kerak bo‘lgan rejalashtirilgan harakatlar uchun foydali." },
    { useCase: "Biznes safar", note: "Mehmonxona transferlari, hosted meeting’lar va sokin shahar jadvali uchun understated executive comfort." },
  ],
  ru: [
    { useCase: "Executive travel", note: "Флагманский седан для hosted arrivals, перемещений старшего руководства и выверенного городского ритма." },
    { useCase: "VIP группы", note: "Сильный выбор для семейных прилётов, премиальных аэропортовых поездок и гостей с большим багажом и запросом на приватность." },
    { useCase: "Семейные поездки", note: "Комфортный формат для семей и небольших private-групп, которым нужна тишина и пространство." },
    { useCase: "Премиальный комфорт", note: "Современный вариант для гостей, которым нужно больше пространства без перехода на более крупный group vehicle." },
    { useCase: "Групповые поездки", note: "Подходит для делегаций, больших семей и запланированных переездов с увеличенной вместимостью багажа." },
    { useCase: "Business travel", note: "Сдержанный executive comfort для трансферов в отели, hosted-встреч и спокойных городских графиков." },
  ],
};

const COMPARISON_TRANSLATIONS = {
  ar: [
    ["تنقل لحظي", "تنسيق مسبق مع سائق خاص"],
    ["برنامج عام", "تخطيط رحلة منسقة"],
    ["حركة جماعية مزدحمة", "تنقل خاص ومناسب للعائلة"],
    ["دعم محدود", "تنسيق عبر واتساب"],
    ["جودة مركبة غير مؤكدة", "معايير أسطول فاخر"],
    ["تخطيط مجزأ", "تصميم تجربة من البداية للنهاية"],
  ],
  uz: [
    ["Oxirgi daqiqadagi transport", "Oldindan tayyorlangan chauffeur koordinatsiyasi"],
    ["Generic marshrut oqimi", "Kuratsiyalangan safar rejalashtiruvi"],
    ["Tiqilinch guruh harakati", "Xususiy va oilaga mos safar"],
    ["Cheklangan yordam", "WhatsApp asosidagi koordinatsiya"],
    ["Noaniq avtomobil sifati", "Premium avtopark standarti"],
    ["Bo‘laklangan reja", "Boshidan oxirigacha tajriba dizayni"],
  ],
  ru: [
    ["Транспорт в последний момент", "Предварительно организованная chauffeur-координация"],
    ["Шаблонный маршрут", "Curated-планирование поездки"],
    ["Перегруженное групповое движение", "Частный и family-friendly формат"],
    ["Ограниченная поддержка", "WhatsApp-координация"],
    ["Неясное качество автомобиля", "Премиальные стандарты автопарка"],
    ["Фрагментированное планирование", "Сквозной дизайн путешествия"],
  ],
};

const PROCESS_TRANSLATIONS = {
  ar: [
    "شارك الوجهة وتواريخ السفر",
    "أخبرنا بتفضيلاتك",
    "استلم خطة تجربة منسقة",
    "أكد البرنامج وما يشمله",
    "وصل وسافر بسلاسة",
  ],
  uz: [
    "Yo‘nalish va sanalarni ulashing",
    "Afzalliklaringizni ayting",
    "Kuratsiyalangan tajriba rejasini oling",
    "Marshrut va kiritmalarni tasdiqlang",
    "Kelish va silliq sayohat qiling",
  ],
  ru: [
    "Поделитесь направлением и датами",
    "Расскажите о предпочтениях",
    "Получите curated-план путешествия",
    "Подтвердите маршрут и включения",
    "Прилетайте и путешествуйте бесшовно",
  ],
};

const EXPERIENCE_TYPE_TRANSLATIONS = {
  ar: ["تجربة عائلية فاخرة", "تجربة سفر أعمال تنفيذية", "تجربة عمرة وزيارات", "تجربة مجموعات الشركات", "رحلة خاصة مخصصة"],
  uz: ["Premium oilaviy tajriba", "Ijrochi biznes safari", "Umra va ziyorat tajribasi", "Korporativ guruh tajribasi", "Moslashtirilgan xususiy safar"],
  ru: ["Премиальный семейный формат", "Исполнительский business-travel формат", "Опыт Умры и зиярата", "Формат для корпоративных групп", "Индивидуальное частное путешествие"],
};

const FAQ_TRANSLATIONS = {
  ar: [
    { question: "ما هي BlackRide Experience؟", answer: "هي خدمة تنسيق سفر راقية تجمع النقل الخاص مع السائق، والبرامج المنسقة، والاستقبال في المطار، والدعم المحلي الموثوق للضيوف الذين يريدون رحلة أكثر سلاسة." },
    { question: "هل تقتصر BlackRide Experience على السعودية؟", answer: "لا. تغطي الخدمة حالياً السعودية وأوزبكستان. الرحلات السعودية مدعومة بخدمات بلاك رايد الفاخرة، بينما تُصمم رحلة أوزبكستان وتُستضاف من قبل HorizonCrew Uzbekistan." },
    { question: "هل يمكنني حجز تجربة سفر للعمرة؟", answer: "نعم. يمكن للضيوف الاستفسار عن تجربة عمرة وزيارات راقية مبنية حول الوصول للمطار والتنقلات الخاصة بين المدن والتخطيط اليومي المنسق في السعودية." },
    { question: "هل توفر بلاك رايد انتقالات المطار في السعودية؟", answer: "نعم. الوصولات من وإلى المطار، والتنقلات الخاصة بين المدن، والحركة المحلية الراقية جزء أساسي من التجربة السعودية." },
    { question: "هل يمكن تخصيص البرنامج؟", answer: "نعم. يمكن تخصيص رحلات السعودية وأوزبكستان بحسب شهر السفر، وعدد الضيوف، وتفضيلات الفندق، وأولوية المسارات، والإيقاع العام للرحلة." },
    { question: "من يشغّل برنامج أوزبكستان؟", answer: "برنامج أوزبكستان يتم تصميمه واستضافته من قبل HorizonCrew Uzbekistan مع تنسيق ميداني ومساعدة إرشادية طوال الرحلة." },
    { question: "هل الرحلات الجوية مشمولة؟", answer: "لا ينبغي افتراض شمول الرحلات الجوية. يتم تأكيد نطاق البرنامج وما يشمله خلال الاستفسار." },
    { question: "هل الفنادق مشمولة؟", answer: "تعتمد فئة الفندق وترتيبات الإقامة على الباقة النهائية المؤكدة، ويتم مناقشتها أثناء الاستفسار." },
    { question: "هل التأشيرات مشمولة؟", answer: "لا ينبغي افتراض معالجة التأشيرات أو الموافقات. يجب على المسافرين التحقق من متطلباتهم عبر القنوات الرسمية ذات الصلة." },
    { question: "هل يمكن للعائلات حجز جولات خاصة؟", answer: "نعم. التجربة مناسبة جداً للعائلات التي ترغب في تنقل خاص، وتخطيط أفضل للأمتعة، وإيقاع سفر أكثر راحة." },
    { question: "هل يمكن لرجال الأعمال حجز تجارب سفر راقية؟", answer: "نعم. صُممت BlackRide Experience للمديرين والضيوف المهمين ومجموعات الأعمال التي تريد مستوى وصول وحركة أكثر أناقة." },
    { question: "كيف يمكنني الاستفسار؟", answer: "يمكنك الاستفسار عبر النموذج في هذه الصفحة أو عبر واتساب مع مشاركة الوجهة والتواريخ وعدد الضيوف والتفضيلات. يتم تأكيد البرنامج النهائي وما يشمله وفئة الفندق والأنشطة والأسعار أثناء الاستفسار." },
  ],
  uz: [
    { question: "BlackRide Experience nima?", answer: "Bu premium travel coordination xizmati bo‘lib, chauffeur-led transport, kuratsiyalangan marshrutlar, aeroportda kutib olish va ishonchli mahalliy yordamni birlashtiradi." },
    { question: "BlackRide Experience faqat Saudiya uchunmi?", answer: "Yo‘q. Hozirda Saudiya Arabistoni va O‘zbekistonni qamrab oladi. Saudiya safarlari BlackRide premium transporti bilan, O‘zbekiston marshruti esa HorizonCrew Uzbekistan tomonidan joyida olib boriladi." },
    { question: "Umra sayohatini bron qilsam bo‘ladimi?", answer: "Ha. Mehmonlar aeroportdan kelish, xususiy shaharlararo harakat va kuratsiyalangan kunlik reja asosida premium Umra va ziyorat tajribasi bo‘yicha murojaat qilishlari mumkin." },
    { question: "BlackRide Saudiyada aeroport transferlarini taqdim etadimi?", answer: "Ha. Aeroportdan kutib olish, xususiy shaharlararo transfer va premium mahalliy harakat Saudiya tajribasining asosiy qismidir." },
    { question: "Marshrutni moslashtirish mumkinmi?", answer: "Ha. Saudiya va O‘zbekiston tajribalari safar oyi, mehmonlar soni, mehmonxona talabi va umumiy safar ritmiga qarab moslashtiriladi." },
    { question: "O‘zbekiston marshrutini kim boshqaradi?", answer: "O‘zbekiston marshruti HorizonCrew Uzbekistan tomonidan ishlab chiqiladi va joyida to‘liq koordinatsiya qilinadi." },
    { question: "Parvozlar kiritilganmi?", answer: "Parvozlar avtomatik ravishda kiritilgan deb hisoblanmaydi. Yakuniy kiritmalar so‘rov jarayonida tasdiqlanadi." },
    { question: "Mehmonxonalar kiritilganmi?", answer: "Mehmonxona darajasi va turar joy tartibi yakuniy paketga bog‘liq bo‘lib, so‘rov davomida aniqlashtiriladi." },
    { question: "Vizalar kiritilganmi?", answer: "Viza jarayoni yoki tasdiqlari kiritilgan deb hisoblanmaydi. Sayohatchilar rasmiy manbalar orqali talablarni o‘zlari tekshirishlari kerak." },
    { question: "Oilalar xususiy turlarni bron qila oladimi?", answer: "Ha. Ushbu tajriba oilalar uchun ayniqsa qulay, chunki u xususiy harakat, yukni rejalash va tinch ritmni ta’minlaydi." },
    { question: "Biznes sayohatchilar premium travel tajribasini buyurtma qila oladimi?", answer: "Ha. BlackRide Experience rahbarlar, VIP mehmonlar va biznes guruhlari uchun mo‘ljallangan." },
    { question: "Qanday murojaat qilaman?", answer: "Sahifadagi forma orqali yoki WhatsApp’da yo‘nalish, sanalar, mehmonlar soni va afzalliklarni yuborib murojaat qilishingiz mumkin. Yakuniy marshrut va kiritmalar so‘rov davomida tasdiqlanadi." },
  ],
  ru: [
    { question: "Что такое BlackRide Experience?", answer: "Это премиальный формат travel-координации, который объединяет chauffeur-led transport, curated-маршруты, встречу в аэропорту и надёжную локальную поддержку для более бесшовной поездки." },
    { question: "BlackRide Experience работает только в Саудовской Аравии?", answer: "Нет. Сейчас формат охватывает Саудовскую Аравию и Узбекистан. Саудовские маршруты поддерживаются сервисом BlackRide, а маршрут по Узбекистану разрабатывается и проводится HorizonCrew Uzbekistan." },
    { question: "Можно ли забронировать поездку для Умры?", answer: "Да. Гости могут запросить премиальный маршрут Умры и зиярата, выстроенный вокруг прилёта, частных междугородних переездов и курируемого дневного ритма." },
    { question: "Предоставляет ли BlackRide аэропортовые трансферы в Саудии?", answer: "Да. Прилёты, частные междугородние трансферы и премиальные локальные перемещения — базовая часть саудовского формата." },
    { question: "Можно ли персонализировать маршрут?", answer: "Да. Маршруты по Саудии и Узбекистану можно адаптировать под месяц поездки, количество гостей, предпочтения по отелю и общий темп путешествия." },
    { question: "Кто проводит маршрут по Узбекистану?", answer: "Маршрут по Узбекистану разработан и проводится HorizonCrew Uzbekistan с наземной координацией и сопровождением." },
    { question: "Включены ли перелёты?", answer: "Не следует считать перелёты включёнными автоматически. Финальный состав путешествия подтверждается на этапе запроса." },
    { question: "Включены ли отели?", answer: "Категория отеля и размещение зависят от финально подтверждённого пакета и согласовываются во время запроса." },
    { question: "Включены ли визы?", answer: "Не следует считать визовую поддержку включённой. Путешественникам следует самостоятельно проверять требования через официальные каналы." },
    { question: "Могут ли семьи забронировать частные туры?", answer: "Да. Формат особенно подходит семьям, которым нужны приватные перемещения, лучшая логистика багажа и более спокойный ритм." },
    { question: "Подходит ли это для business travellers?", answer: "Да. BlackRide Experience создан для руководителей, hosted-гостей, VIP-персон и деловых групп, которым нужен более высокий стандарт передвижения." },
    { question: "Как отправить запрос?", answer: "Вы можете оставить запрос через форму на странице или написать в WhatsApp, указав направление, даты, число гостей и предпочтения. Финальный маршрут и включения подтверждаются на этапе запроса." },
  ],
};

const RELATED_LINK_TRANSLATIONS = {
  ar: ["الصفحة الرئيسية لبلاك رايد", "خدمة سائق فاخر في السعودية", "انتقالات المطار في السعودية", "انتقال مطار جدة", "نقل خاص من مطار جدة إلى مكة", "خدمة سائق من مكة إلى المدينة", "نقل العمرة في السعودية", "مدونة بلاك رايد"],
  uz: ["BlackRide bosh sahifasi", "Saudiyada premium chauffeur xizmati", "Saudiyada aeroport transferi", "Jidda aeroport transferi", "Jidda aeroportidan Makkahga xususiy transfer", "Makkahdan Madinaga chauffeur xizmati", "Saudiyada Umra transporti", "BlackRide blogi"],
  ru: ["Главная BlackRide", "Премиальный chauffeur-сервис в Саудии", "Аэропортовые трансферы в Саудии", "Трансфер из аэропорта Джидды", "Частный трансфер из аэропорта Джидды в Мекку", "Chauffeur-сервис из Мекки в Медину", "Транспорт для Умры в Саудии", "Блог BlackRide"],
};

function ensureMeta(selector, identity, content) {
  let node = document.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    Object.entries(identity).forEach(([key, value]) => node.setAttribute(key, value));
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
}

function buildWhatsappLink(message) {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

function scrollToId(id) {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function SectionIntro({ eyebrow, title, body, align = "left" }) {
  return (
    <div className={`max-w-4xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      <p className="text-xs uppercase tracking-[0.28em] text-[#C7A239] font-medium mb-4">{eyebrow}</p>
      <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl leading-tight text-white mb-4">{title}</h2>
      {body ? <p className="text-[15px] md:text-lg leading-8 text-[#d4d0cb]">{body}</p> : null}
    </div>
  );
}

function JourneyTimeline({ items, accent = "Saudi Arabia", stopLabel = "Stop" }) {
  return (
    <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
      {items.map((item, index) => (
        <article
          key={item.title}
          className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))] p-6 shadow-[0_28px_80px_rgba(0,0,0,0.35)]"
        >
          {item.image ? (
            <div className="mb-5 overflow-hidden rounded-[1.3rem] border border-white/8 bg-black/20">
              <img
                src={item.image}
                alt={item.alt || item.title}
                className="h-44 w-full object-cover"
                width="640"
                height="480"
                loading="lazy"
              />
            </div>
          ) : null}
          <div className="flex items-center justify-between gap-4 mb-5">
            <span className="inline-flex rounded-full border border-[#C7A239]/40 bg-[#C7A239]/10 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-[#E3C974]">
              {item.day}
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/35">
              {accent} · {stopLabel} {index + 1}
            </span>
          </div>
          <h3 className="font-['Playfair_Display'] text-2xl text-white mb-4 leading-snug">{item.title}</h3>
          <ul className="space-y-3">
            {item.items.map((detail) => (
              <li key={detail} className="flex gap-3 text-sm leading-7 text-[#d6d1ca]">
                <span className="mt-[9px] h-2 w-2 shrink-0 rounded-full bg-[#C7A239]" />
                <span>{detail}</span>
              </li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

function ExperienceHeroSlider({ onScrollToId, slides, isRTL, trustCopy, ratingCopy }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const touchStartX = useRef(null);
  const slideCount = slides.length;
  const activeSlide = slides[activeIndex];

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => setReducedMotion(mediaQuery.matches);

    updateMotionPreference();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateMotionPreference);
      return () => mediaQuery.removeEventListener("change", updateMotionPreference);
    }

    mediaQuery.addListener(updateMotionPreference);
    return () => mediaQuery.removeListener(updateMotionPreference);
  }, []);

  useEffect(() => {
    if (reducedMotion) return undefined;

    const timer = window.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % slideCount);
    }, HERO_AUTOPLAY_MS);

    return () => window.clearTimeout(timer);
  }, [activeIndex, reducedMotion, slideCount]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const targetTag = event.target?.tagName?.toLowerCase();
      if (targetTag === "input" || targetTag === "textarea" || targetTag === "select") return;

      if (event.key === "ArrowRight") {
        setActiveIndex((current) => (current + 1) % slideCount);
      } else if (event.key === "ArrowLeft") {
        setActiveIndex((current) => (current - 1 + slideCount) % slideCount);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [slideCount]);

  const changeSlide = (index) => {
    setActiveIndex((index + slideCount) % slideCount);
  };

  const handlePrimaryAction = () => {
    if (activeSlide.primaryHref) {
      window.open(activeSlide.primaryHref, "_blank", "noopener,noreferrer");
      return;
    }
    onScrollToId(activeSlide.primaryTarget);
  };

  const handleSecondaryAction = () => {
    if (activeSlide.secondaryHref) {
      window.open(activeSlide.secondaryHref, "_blank", "noopener,noreferrer");
      return;
    }
    if (activeSlide.secondaryMessage) {
      window.open(buildWhatsappLink(activeSlide.secondaryMessage), "_blank", "noopener,noreferrer");
      return;
    }
    onScrollToId(activeSlide.secondaryTarget);
  };

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current == null) return;
    const endX = event.changedTouches[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    touchStartX.current = null;

    if (Math.abs(delta) < 48) return;
    if (delta < 0) {
      setActiveIndex((current) => (current + 1) % slideCount);
    } else {
      setActiveIndex((current) => (current - 1 + slideCount) % slideCount);
    }
  };

  return (
    <section
      aria-label="BlackRide Experience highlights"
      className="experience-hero-slider relative isolate overflow-hidden border-y border-white/10 bg-[#050505]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="pointer-events-none absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={slide.title}
            aria-hidden={activeIndex !== index}
            className={`absolute inset-0 transition-opacity ${reducedMotion ? "duration-0" : "duration-1000 ease-out"} ${
              activeIndex === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className="h-full w-full object-cover"
              style={{ objectPosition: slide.objectPosition }}
              width="1822"
              height="863"
              loading={index === 0 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : "auto"}
            />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.88)_0%,rgba(0,0,0,0.56)_44%,rgba(0,0,0,0.16)_100%)]" />
            <div className="absolute inset-x-0 bottom-0 h-44 bg-[linear-gradient(0deg,rgba(0,0,0,0.9)_0%,rgba(0,0,0,0)_100%)]" />
          </div>
        ))}
      </div>

      <div className="relative mx-auto grid min-h-[calc(100svh-6rem)] max-w-7xl grid-rows-[1fr_auto_auto] px-6 pb-8 pt-12 md:min-h-[calc(100svh-7rem)] md:px-12 md:pb-10 md:pt-16">
        <div />

        <div className={`relative z-10 max-w-3xl pb-8 md:pb-12 ${isRTL ? "ml-auto text-right" : ""}`}>
          <p className="mb-5 text-[11px] font-medium uppercase tracking-[0.34em] text-[#D6AA3D] md:text-xs">
            {activeSlide.eyebrow}
          </p>
          <h2
            className={`max-w-4xl font-['Playfair_Display'] text-[clamp(2.8rem,6vw,6.3rem)] leading-[0.93] text-white transition-all ${
              reducedMotion ? "duration-0" : "duration-700 ease-out"
            }`}
          >
            {activeSlide.title}
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-8 text-white/80 md:text-[1.05rem]">
            {activeSlide.description}
          </p>
          <p className="mt-5 text-sm uppercase tracking-[0.22em] text-white/70 md:text-[0.95rem]">
            {activeSlide.destinationLine}
          </p>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <button
              type="button"
              onClick={handlePrimaryAction}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D6AA3D] px-7 py-3.5 text-sm font-semibold text-black transition hover:bg-[#e0b751] focus:outline-none focus:ring-2 focus:ring-[#D6AA3D] focus:ring-offset-2 focus:ring-offset-black"
            >
              {activeSlide.primaryCta}
            </button>
            <button
              type="button"
              onClick={handleSecondaryAction}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/[0.03] px-7 py-3.5 text-sm font-semibold text-white transition hover:border-[#D6AA3D]/55 hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
            >
              {activeSlide.secondaryCta}
            </button>
          </div>

          <div className={`mt-8 flex flex-col gap-4 rounded-[1.6rem] border border-white/10 bg-black/30 px-5 py-4 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between ${isRTL ? "sm:flex-row-reverse" : ""}`}>
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#D6AA3D]">{trustCopy.kicker}</p>
              <p className="mt-2 text-sm text-white/80">{trustCopy.body}</p>
            </div>
            <div className={`flex items-center gap-4 ${isRTL ? "sm:justify-start" : "sm:justify-end"}`}>
              <div className="flex items-center gap-1 text-[#D6AA3D]" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star key={index} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{ratingCopy.title}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-white/50">{ratingCopy.subtitle}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={`relative z-10 flex items-center justify-between gap-4 pb-5 ${isRTL ? "flex-row-reverse" : ""}`}>
          <div className={`flex items-center gap-2 ${isRTL ? "flex-row-reverse" : ""}`}>
            {slides.map((slide, index) => (
              <button
                key={slide.previewTitle}
                type="button"
                aria-label={`Go to slide ${index + 1}: ${slide.previewTitle}`}
                aria-pressed={activeIndex === index}
                onClick={() => changeSlide(index)}
                className={`rounded-full border px-3 py-2 text-[11px] font-medium tracking-[0.22em] transition ${
                  activeIndex === index
                    ? "border-[#D6AA3D] bg-[#D6AA3D]/12 text-[#D6AA3D]"
                    : "border-white/15 bg-black/20 text-white/65 hover:border-white/35 hover:text-white"
                }`}
              >
                {(index + 1).toString().padStart(2, "0")}
              </button>
            ))}
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <button
              type="button"
              aria-label="Previous slide"
              onClick={() => setActiveIndex((current) => (current - 1 + slideCount) % slideCount)}
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-black/35 text-white transition hover:border-[#D6AA3D]/55 hover:text-[#D6AA3D] focus:outline-none focus:ring-2 focus:ring-[#D6AA3D] focus:ring-offset-2 focus:ring-offset-black"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next slide"
              onClick={() => setActiveIndex((current) => (current + 1) % slideCount)}
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-black/35 text-white transition hover:border-[#D6AA3D]/55 hover:text-[#D6AA3D] focus:outline-none focus:ring-2 focus:ring-[#D6AA3D] focus:ring-offset-2 focus:ring-offset-black"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="relative z-10 -mx-2 overflow-x-auto px-2 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex min-w-max gap-4 snap-x snap-mandatory xl:min-w-0 xl:grid xl:grid-cols-5">
            {slides.map((slide, index) => (
              <button
                key={slide.previewTitle}
                type="button"
                onClick={() => changeSlide(index)}
                aria-label={`Preview ${slide.previewTitle}`}
                className={`group relative h-[220px] w-[280px] snap-start overflow-hidden rounded-[1.5rem] border text-left shadow-[0_24px_80px_rgba(0,0,0,0.35)] transition xl:w-auto ${
                  activeIndex === index
                    ? "border-[#D6AA3D] ring-1 ring-[#D6AA3D]/45"
                    : "border-white/10 hover:-translate-y-1 hover:border-[#D6AA3D]/35"
                }`}
              >
                <img
                  src={slide.image}
                  alt={slide.alt}
                  className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                  style={{ objectPosition: slide.objectPosition }}
                  width="1822"
                  height="863"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.08)_0%,rgba(0,0,0,0.78)_72%,rgba(0,0,0,0.92)_100%)]" />
                <div className="relative flex h-full flex-col justify-end p-5">
                  <p className="text-[11px] uppercase tracking-[0.26em] text-[#D6AA3D]">
                    {(index + 1).toString().padStart(2, "0")}
                  </p>
                  <h2 className="mt-3 font-['Playfair_Display'] text-2xl leading-tight text-white">{slide.previewTitle}</h2>
                  <p className="mt-2 text-xs uppercase tracking-[0.18em] text-white/65">{slide.previewSubtitle}</p>
                  <p className="mt-3 text-sm leading-6 text-white/78">{slide.previewDescription}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const defaultForm = {
  fullName: "",
  whatsapp: "",
  email: "",
  destination: "Saudi Arabia",
  travelMonth: "",
  guests: "",
  experienceType: "Premium Family Experience",
  message: "",
};

export default function BlackRideExperience() {
  const { lang, isRTL } = useLanguage();
  const [form, setForm] = useState(defaultForm);
  const currentCopy = PAGE_COPY[lang] || PAGE_COPY.en;

  const localizedSlides = useMemo(
    () =>
      heroSlides.map((slide, index) => ({
        ...slide,
        ...(HERO_TRANSLATIONS[lang]?.[index] || {}),
      })),
    [lang],
  );

  const localizedTrustPoints = useMemo(
    () =>
      trustPoints.map((point, index) => ({
        ...point,
        label: TRUST_POINT_TRANSLATIONS[lang]?.[index] || point.label,
      })),
    [lang],
  );

  const localizedDestinationCards = useMemo(
    () =>
      destinationCards.map((card, index) => ({
        ...card,
        ...(DESTINATION_CARD_TRANSLATIONS[lang]?.[index] || {}),
      })),
    [lang],
  );

  const localizedWhyCards = useMemo(
    () =>
      whyCards.map((card, index) => ({
        ...card,
        ...(WHY_CARD_TRANSLATIONS[lang]?.[index] || {}),
      })),
    [lang],
  );

  const localizedFeaturedExperiences = useMemo(
    () =>
      featuredExperiences.map((item, index) => ({
        ...item,
        ...(FEATURED_TRANSLATIONS[lang]?.[index] || {}),
      })),
    [lang],
  );

  const localizedSaudiTimeline = useMemo(
    () =>
      saudiTimeline.map((item, index) => ({
        ...item,
        ...(SAUDI_TIMELINE_TRANSLATIONS[lang]?.[index] || {}),
      })),
    [lang],
  );

  const localizedUzbekistanTimeline = useMemo(
    () =>
      uzbekistanTimeline.map((item, index) => ({
        ...item,
        ...(UZBEKISTAN_TIMELINE_TRANSLATIONS[lang]?.[index] || {}),
      })),
    [lang],
  );

  const localizedFleetCards = useMemo(
    () =>
      fleetCards.map((item, index) => ({
        ...item,
        ...(FLEET_TRANSLATIONS[lang]?.[index] || {}),
      })),
    [lang],
  );

  const localizedComparisonRows = useMemo(() => COMPARISON_TRANSLATIONS[lang] || comparisonRows, [lang]);
  const localizedProcessSteps = useMemo(() => PROCESS_TRANSLATIONS[lang] || processSteps, [lang]);
  const localizedExperienceTypes = useMemo(() => EXPERIENCE_TYPE_TRANSLATIONS[lang] || experienceTypes, [lang]);
  const localizedFaqs = useMemo(() => FAQ_TRANSLATIONS[lang] || faqs, [lang]);
  const relatedServiceLabels = useMemo(() => RELATED_LINK_TRANSLATIONS[lang], [lang]);
  const timelineStopLabel = useMemo(
    () =>
      ({
        en: "Stop",
        ar: "محطة",
        uz: "Bosqich",
        ru: "Этап",
      })[lang] || "Stop",
    [lang],
  );
  const destinationOptionLabels = useMemo(
    () =>
      ({
        en: { saudi: "Saudi Arabia", uzbekistan: "Uzbekistan", both: "Both" },
        ar: { saudi: "السعودية", uzbekistan: "أوزبكستان", both: "كلاهما" },
        uz: { saudi: "Saudiya Arabistoni", uzbekistan: "O‘zbekiston", both: "Ikkalasi ham" },
        ru: { saudi: "Саудовская Аравия", uzbekistan: "Узбекистан", both: "Оба направления" },
      })[lang] || { saudi: "Saudi Arabia", uzbekistan: "Uzbekistan", both: "Both" },
    [lang],
  );
  const saudiLinkLabels = useMemo(
    () =>
      ({
        en: [
          "Jeddah Airport to Makkah private transfer",
          "Makkah to Madinah private transfer",
          "Umrah transport in Saudi Arabia",
        ],
        ar: [
          "نقل خاص من مطار جدة إلى مكة",
          "نقل خاص من مكة إلى المدينة",
          "نقل العمرة في السعودية",
        ],
        uz: [
          "Jidda aeroportidan Makkahga xususiy transfer",
          "Makkahdan Madinaga xususiy transfer",
          "Saudiyada Umra transporti",
        ],
        ru: [
          "Частный трансфер из аэропорта Джидды в Мекку",
          "Частный трансфер из Мекки в Медину",
          "Транспорт для Умры в Саудии",
        ],
      })[lang] || [],
    [lang],
  );

  const showTranslationNote = false;

  useEffect(() => {
    document.title = META_TITLE;
    ensureMeta('meta[name="description"]', { name: "description" }, META_DESCRIPTION);
    ensureMeta('meta[property="og:title"]', { property: "og:title" }, "BlackRide Experience | Premium Saudi & Uzbekistan Travel Packages");
    ensureMeta('meta[property="og:description"]', { property: "og:description" }, OG_DESCRIPTION);
    ensureMeta('meta[property="og:url"]', { property: "og:url" }, PAGE_URL);
    ensureMeta('meta[property="og:type"]', { property: "og:type" }, "website");
    ensureMeta('meta[property="og:image"]', { property: "og:image" }, OG_IMAGE);
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image");
    ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" }, META_TITLE);
    ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" }, META_DESCRIPTION);
    ensureMeta('meta[name="twitter:image"]', { name: "twitter:image" }, OG_IMAGE);

    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute("href", PAGE_URL);

  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlTheme = html.getAttribute("data-theme");
    const previousBodyTheme = body.getAttribute("data-theme");

    html.setAttribute("data-theme", "dark");
    body.setAttribute("data-theme", "dark");

    return () => {
      if (previousHtmlTheme) {
        html.setAttribute("data-theme", previousHtmlTheme);
      } else {
        html.removeAttribute("data-theme");
      }
      if (previousBodyTheme) {
        body.setAttribute("data-theme", previousBodyTheme);
      } else {
        body.removeAttribute("data-theme");
      }
    };
  }, []);

  const breadcrumbs = useMemo(
    () => [
      { name: currentCopy.home, item: "/" },
      { name: currentCopy.experience, item: "/experience" },
    ],
    [currentCopy.experience, currentCopy.home],
  );

  const schemaGraph = useMemo(
    () =>
      buildPageSchemaGraph([
        buildOrganizationSchema(),
        buildLocalBusinessSchema({
          description: META_DESCRIPTION,
          url: PAGE_URL,
          image: OG_IMAGE,
          areaServed: [
            "Saudi Arabia",
            "Jeddah",
            "Makkah",
            "Madinah",
            "Riyadh",
            "Dammam",
            "Uzbekistan",
            "Tashkent",
            "Samarkand",
            "Bukhara",
          ],
        }),
        buildServiceSchema({
          name: "BlackRide Experience",
          description:
            "Curated premium journeys across Saudi Arabia and Uzbekistan with chauffeur-led travel, airport meet and greet, guided assistance, premium vehicles, and trusted local coordination.",
          url: PAGE_URL,
          serviceType: "Premium travel coordination and curated private journeys",
          areaServed: [
            "Saudi Arabia",
            "Jeddah",
            "Makkah",
            "Madinah",
            "Riyadh",
            "Dammam",
            "Uzbekistan",
            "Tashkent",
            "Samarkand",
            "Bukhara",
          ],
          audience:
            "premium business travellers, high-net-worth families, GCC travellers, Indian premium travellers, corporate groups, Umrah travellers, VIP guests, and private family groups",
        }),
        buildBreadcrumbSchema(breadcrumbs),
        buildFaqSchema(localizedFaqs),
      ]),
    [breadcrumbs, localizedFaqs],
  );

  const heroWhatsapp = buildWhatsappLink(
    "Hi BlackRide, I am interested in BlackRide Experience. Please help me plan a premium trip to Saudi Arabia / Uzbekistan.",
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const message = [
      "Hi BlackRide, I am interested in BlackRide Experience.",
      `Full Name: ${form.fullName || "-"}`,
      `WhatsApp: ${form.whatsapp || "-"}`,
      `Email: ${form.email || "-"}`,
      `Destination: ${form.destination || "-"}`,
      `Travel Month: ${form.travelMonth || "-"}`,
      `Number of Guests: ${form.guests || "-"}`,
      `Preferred Experience Type: ${form.experienceType || "-"}`,
      `Requirements: ${form.message || "-"}`,
    ].join("\n");

    window.open(buildWhatsappLink(message), "_blank", "noopener,noreferrer");
  };

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="min-h-screen overflow-x-hidden bg-[#040404] pt-24 text-white md:pt-28">
      <SeoJsonLd id="blackride-experience-schema" data={schemaGraph} />

      <ExperienceHeroSlider
        onScrollToId={scrollToId}
        slides={localizedSlides}
        isRTL={isRTL}
        trustCopy={currentCopy.trust}
        ratingCopy={currentCopy.rating}
      />

      <section className="px-6 py-4 md:px-12">
        <div className={`mx-auto flex max-w-7xl flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.22em] text-white/35 ${isRTL ? "justify-end" : ""}`}>
          <div className={`flex flex-wrap gap-2 ${isRTL ? "justify-end" : ""}`}>
            {breadcrumbs.map((item, index) => (
              <span key={item.item} className="inline-flex items-center gap-2">
                {index === breadcrumbs.length - 1 ? (
                  <span className="text-[#C7A239]">{item.name}</span>
                ) : (
                  <Link to={item.item} className="transition hover:text-white">
                    {item.name}
                  </Link>
                )}
                {index < breadcrumbs.length - 1 ? <span>/</span> : null}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 md:px-12 md:pb-24">
        <div className="mx-auto max-w-7xl grid gap-10 xl:grid-cols-[1.05fr_0.95fr] items-stretch">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#0a0a0a] p-8 md:p-12 shadow-[0_32px_120px_rgba(0,0,0,0.45)]">
            <div className="absolute inset-0">
              <img
                src={MEDIA.hero}
                alt="Luxury destination travel across Saudi Arabia and Uzbekistan"
                className="h-full w-full object-cover"
                width="1600"
                height="1100"
                loading="eager"
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(4,4,4,0.94)_0%,rgba(4,4,4,0.78)_42%,rgba(4,4,4,0.42)_100%)]" />
            </div>
            <div className="relative z-10 max-w-2xl">
              <p className="mb-5 text-xs font-medium uppercase tracking-[0.3em] text-[#C7A239]">
                {localizedSlides[0].eyebrow}
              </p>
              <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl xl:text-7xl leading-[0.95] text-white mb-6">
                {localizedSlides[0].title}
              </h1>
              <p className="text-lg md:text-xl leading-8 text-[#dbd5ce] max-w-xl mb-8">
                {localizedSlides[0].description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollToId("saudi-experience")}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C7A239] px-6 py-3.5 text-sm font-semibold text-black transition hover:bg-[#d8b14d]"
                >
                  {localizedSlides[0].primaryCta}
                </button>
                <button
                  onClick={() => scrollToId("uzbekistan-experience")}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/[0.03] px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-white hover:text-black"
                >
                  {localizedSlides[0].secondaryCta}
                </button>
              </div>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-1">
            <article className="overflow-hidden rounded-[2rem] border border-white/10 bg-[#0c0c0c] shadow-[0_20px_70px_rgba(0,0,0,0.35)]">
              <img
                src={MEDIA.saudi}
                alt="Saudi Arabia premium city and spiritual destination mood"
                className="h-56 w-full object-cover"
                width="1200"
                height="800"
                loading="eager"
              />
              <div className="p-6">
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#C7A239] mb-3">{localizedDestinationCards[0].eyebrow}</p>
                <h2 className="font-['Playfair_Display'] text-3xl text-white mb-3">{localizedDestinationCards[0].title}</h2>
                <p className="text-sm leading-7 text-[#cec7be]">
                  {localizedDestinationCards[0].description}
                </p>
              </div>
            </article>

            <article className="overflow-hidden rounded-[2rem] border border-white/10 bg-[#0c0c0c] shadow-[0_20px_70px_rgba(0,0,0,0.35)]">
              <img
                src={MEDIA.uzbekistan}
                alt="Uzbekistan heritage and Silk Road cultural destination mood"
                className="h-56 w-full object-cover"
                width="1200"
                height="800"
                loading="eager"
              />
              <div className="p-6">
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#C7A239] mb-3">{localizedDestinationCards[1].eyebrow}</p>
                <h2 className="font-['Playfair_Display'] text-3xl text-white mb-3">{localizedDestinationCards[1].title}</h2>
                <p className="text-sm leading-7 text-[#cec7be]">
                  {localizedDestinationCards[1].description}
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#070707] px-6 py-6 md:px-12">
        <div className="mx-auto max-w-7xl grid gap-3 md:grid-cols-3 xl:grid-cols-6">
          {localizedTrustPoints.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.label}
                className="flex items-center gap-3 rounded-2xl border border-white/8 bg-white/[0.02] px-4 py-4"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#C7A239]/12 text-[#C7A239]">
                  <Icon className="h-4 w-4" />
                </span>
                <p className="text-sm text-[#ddd6cf]">{item.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionIntro
            eyebrow={currentCopy.destinationIntro.eyebrow}
            title={currentCopy.destinationIntro.title}
            body={currentCopy.destinationIntro.body}
          />

          <div className="mt-12 grid gap-8 xl:grid-cols-2">
            {localizedDestinationCards.map((card) => (
              <article
                key={card.title}
                className="group overflow-hidden rounded-[2rem] border border-white/10 bg-[#0b0b0b] shadow-[0_32px_90px_rgba(0,0,0,0.35)]"
              >
                <div className="relative h-[320px] overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.alt}
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                    width="1400"
                    height="960"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.08),rgba(0,0,0,0.78))]" />
                  <div className="absolute bottom-0 left-0 right-0 p-7 md:p-8">
                    <p className="text-[11px] uppercase tracking-[0.28em] text-[#E0C26A] mb-2">{card.eyebrow}</p>
                    <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-2">{card.title}</h3>
                    <p className="text-sm uppercase tracking-[0.18em] text-white/55">{card.subtitle}</p>
                  </div>
                </div>
                <div className="p-7 md:p-8">
                  <p className="text-[15px] leading-8 text-[#d4cec6] mb-6">{card.description}</p>
                  <div className="mb-6 flex flex-wrap gap-3">
                    {card.badges.map((badge) => (
                      <span
                        key={badge}
                        className="rounded-full border border-[#C7A239]/30 bg-[#C7A239]/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-[#E0C26A]"
                      >
                        {badge}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => scrollToId(card.href.replace("#", ""))}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#C7A239] transition hover:text-[#e0c26a]"
                  >
                    {card.cta}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#070707] px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionIntro
            eyebrow={currentCopy.whyIntro.eyebrow}
            title={currentCopy.whyIntro.title}
            body={currentCopy.whyIntro.body}
          />

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {localizedWhyCards.map((card) => (
              <article
                key={card.title}
                className="overflow-hidden rounded-[1.8rem] border border-white/10 bg-[#0c0c0c] shadow-[0_24px_70px_rgba(0,0,0,0.32)]"
              >
                <img
                  src={card.image}
                  alt={card.alt}
                  className="h-52 w-full object-cover"
                  width="1200"
                  height="760"
                  loading="lazy"
                />
                <div className="p-6">
                  <h3 className="font-['Playfair_Display'] text-2xl text-white mb-3">{card.title}</h3>
                  <p className="text-sm leading-7 text-[#d1cbc4]">{card.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionIntro
            eyebrow={currentCopy.featuredIntro.eyebrow}
            title={currentCopy.featuredIntro.title}
            body={currentCopy.featuredIntro.body}
          />

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {localizedFeaturedExperiences.map((item) => (
              <article
                key={item.title}
                className="overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#0c0c0c] shadow-[0_24px_70px_rgba(0,0,0,0.32)]"
              >
                <img
                  src={item.image}
                  alt={`${item.title} destination journey`}
                  className="h-44 w-full object-cover"
                  width="1000"
                  height="760"
                  loading="lazy"
                />
                <div className="p-6">
                  <span className="inline-flex rounded-full border border-[#C7A239]/30 bg-[#C7A239]/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-[#E0C26A] mb-4">
                    {item.tag}
                  </span>
                  <h3 className="font-['Playfair_Display'] text-2xl text-white mb-3">{item.title}</h3>
                  <p className="text-sm leading-7 text-[#d1cbc4] mb-5">{item.description}</p>
                  <button
                    onClick={() => {
                      if (item.href.startsWith("#")) scrollToId(item.href.replace("#", ""));
                    }}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#C7A239] transition hover:text-[#e0c26a]"
                  >
                    {item.cta}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="saudi-experience" className="border-y border-white/10 bg-[#070707] px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionIntro
            eyebrow={currentCopy.saudiIntro.eyebrow}
            title={currentCopy.saudiIntro.title}
            body={currentCopy.saudiIntro.body}
          />

          <div className="mt-8 mb-10 flex flex-wrap gap-4">
            <Link to="/jeddah-airport-to-makkah-transfer" className="rounded-full border border-white/15 px-4 py-2 text-sm text-[#d9d1c8] transition hover:border-[#C7A239]/50 hover:text-white">
              {saudiLinkLabels[0] || "Jeddah Airport to Makkah private transfer"}
            </Link>
            <Link to="/makkah-to-madinah-transfer" className="rounded-full border border-white/15 px-4 py-2 text-sm text-[#d9d1c8] transition hover:border-[#C7A239]/50 hover:text-white">
              {saudiLinkLabels[1] || "Makkah to Madinah private transfer"}
            </Link>
            <Link to="/umrah-transport-saudi-arabia" className="rounded-full border border-white/15 px-4 py-2 text-sm text-[#d9d1c8] transition hover:border-[#C7A239]/50 hover:text-white">
              {saudiLinkLabels[2] || "Umrah transport in Saudi Arabia"}
            </Link>
          </div>

          <JourneyTimeline items={localizedSaudiTimeline} accent={localizedDestinationCards[0].eyebrow} stopLabel={timelineStopLabel} />

          <div className="mt-12 rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,rgba(199,162,57,0.10),rgba(255,255,255,0.02))] p-8 md:p-10">
            <div className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr] items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-[#C7A239] mb-4">{currentCopy.saudiCta.kicker}</p>
                <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-4">
                  {currentCopy.saudiCta.title}
                </h3>
                <p className="text-[15px] leading-8 text-[#d5cec4]">
                  {currentCopy.saudiCta.body}
                </p>
              </div>
              <div className="flex flex-col gap-4">
                <a
                  href={buildWhatsappLink("Hi BlackRide, I would like to plan my Saudi Experience with premium chauffeur support.")}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C7A239] px-6 py-3.5 text-sm font-semibold text-black transition hover:bg-[#d8b14d]"
                >
                  {currentCopy.saudiCta.primary}
                </a>
                <Link
                  to="/chauffeur-service-saudi-arabia"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-white hover:text-black"
                >
                  {currentCopy.saudiCta.secondary}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="uzbekistan-experience" className="px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionIntro
            eyebrow={currentCopy.uzbekIntro.eyebrow}
            title={currentCopy.uzbekIntro.title}
            body={currentCopy.uzbekIntro.body}
          />

          <div className="mt-8 rounded-[1.75rem] border border-[#C7A239]/20 bg-[#0c0c0c] p-5 text-sm leading-7 text-[#d7d0c8]">
            {currentCopy.uzbekIntro.note}
          </div>

          <div className="mt-10">
            <JourneyTimeline items={localizedUzbekistanTimeline} accent={localizedDestinationCards[1].eyebrow} stopLabel={timelineStopLabel} />
          </div>

          <div className="mt-12 rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-8 md:p-10">
            <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr] items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-[#C7A239] mb-4">{currentCopy.uzbekIntro.kicker}</p>
                <h3 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-4">
                  {currentCopy.uzbekIntro.ctaTitle}
                </h3>
                <p className="text-[15px] leading-8 text-[#d5cec4]">
                  {currentCopy.uzbekIntro.ctaBody}
                </p>
              </div>
              <div className="flex flex-col gap-4">
                <a
                  href={buildWhatsappLink("Hi BlackRide, I would like to plan my Uzbekistan Experience with HorizonCrew Uzbekistan.")}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C7A239] px-6 py-3.5 text-sm font-semibold text-black transition hover:bg-[#d8b14d]"
                >
                  {currentCopy.uzbekIntro.primary}
                </a>
                <a
                  href="#enquire"
                  onClick={(event) => {
                    event.preventDefault();
                    scrollToId("enquire");
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-white hover:text-black"
                >
                  {currentCopy.uzbekIntro.secondary}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#070707] px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionIntro
            eyebrow={currentCopy.fleetIntro.eyebrow}
            title={currentCopy.fleetIntro.title}
            body={currentCopy.fleetIntro.body}
          />

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {localizedFleetCards.map((item) => (
              <article
                key={item.name}
                className="overflow-hidden rounded-[1.8rem] border border-white/10 bg-[#0b0b0b] shadow-[0_24px_70px_rgba(0,0,0,0.32)]"
              >
                <img
                  src={item.image}
                  alt={`${item.name} premium vehicle for chauffeur-led travel`}
                  className="h-52 w-full object-cover"
                  width="1200"
                  height="760"
                  loading="lazy"
                />
                <div className="p-6">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#C7A239] mb-3">{item.useCase}</p>
                  <h3 className="font-['Playfair_Display'] text-2xl text-white mb-3">{item.name}</h3>
                  <p className="text-sm leading-7 text-[#d1cbc4]">{item.note}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl grid gap-10 xl:grid-cols-[0.9fr_1.1fr] items-start">
          <SectionIntro
            eyebrow={currentCopy.differenceIntro.eyebrow}
            title={currentCopy.differenceIntro.title}
            body={currentCopy.differenceIntro.body}
          />

          <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-[#0b0b0b]">
            {localizedComparisonRows.map(([standard, elevated]) => (
              <div
                key={`${standard}-${elevated}`}
                className="grid gap-4 border-b border-white/8 px-6 py-5 md:grid-cols-2 last:border-b-0"
              >
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-white/35 mb-2">{currentCopy.differenceIntro.standard}</p>
                  <p className="text-sm leading-7 text-[#c7c1ba]">{standard}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#C7A239] mb-2">{currentCopy.differenceIntro.elevated}</p>
                  <p className="text-sm leading-7 text-white">{elevated}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#070707] px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionIntro
            eyebrow={currentCopy.howItWorks.eyebrow}
            title={currentCopy.howItWorks.title}
            body={currentCopy.howItWorks.body}
            align="center"
          />

          <div className="mt-12 grid gap-5 md:grid-cols-5">
            {localizedProcessSteps.map((step, index) => (
              <article
                key={step}
                className="rounded-[1.6rem] border border-white/10 bg-[#0b0b0b] p-6 text-center shadow-[0_18px_50px_rgba(0,0,0,0.28)]"
              >
                <span className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#C7A239] text-sm font-semibold text-black">
                  {index + 1}
                </span>
                <p className="text-sm leading-7 text-[#d6d0c8]">{step}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="enquire" className="px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl grid gap-10 xl:grid-cols-[0.9fr_1.1fr] items-start">
          <div>
            <div className="mb-8 overflow-hidden rounded-[2rem] border border-white/10 bg-[#0c0c0c] shadow-[0_24px_70px_rgba(0,0,0,0.32)]">
              <img
                src={MEDIA.cta}
                alt="Executive guest with luggage beside a premium chauffeur vehicle outside a luxury arrival entrance"
                className="h-72 w-full object-cover"
                width="1400"
                height="920"
                loading="lazy"
              />
            </div>
            <SectionIntro
              eyebrow={currentCopy.enquire.eyebrow}
              title={currentCopy.enquire.title}
              body={currentCopy.enquire.body}
            />

            <div className="mt-8 space-y-4 text-sm leading-7 text-[#d3cdc5]">
              {currentCopy.enquire.bullets.map((bullet) => (
                <p key={bullet} className="flex gap-3">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-[#C7A239]" />
                  <span>{bullet}</span>
                </p>
              ))}
            </div>

            {showTranslationNote ? (
              <p className="mt-6 text-xs leading-6 text-white/45">
                Some detailed itinerary and supporting card copy still appears in English while the full multilingual
                rollout is completed.
              </p>
            ) : null}

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={heroWhatsapp}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-[#C7A239]/35 bg-[#C7A239]/10 px-5 py-3 text-sm font-semibold text-[#E0C26A] transition hover:bg-[#C7A239]/20"
              >
                <MessageCircle className="h-4 w-4" />
                {currentCopy.enquire.whatsapp}
              </a>
              <a
                href="mailto:hello@getblackride.com?subject=BlackRide%20Experience%20Enquiry"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white hover:text-black"
              >
                {currentCopy.enquire.email}
              </a>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-[2rem] border border-white/10 bg-[#0b0b0b] p-6 md:p-8 shadow-[0_28px_90px_rgba(0,0,0,0.35)]"
          >
            <div className="grid gap-5 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm text-[#d7d0c8]">{currentCopy.enquire.fullName}</span>
                <input
                  type="text"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-white outline-none transition focus:border-[#C7A239]/60"
                  required
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm text-[#d7d0c8]">{currentCopy.enquire.whatsappNumber}</span>
                <input
                  type="text"
                  name="whatsapp"
                  value={form.whatsapp}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-white outline-none transition focus:border-[#C7A239]/60"
                  required
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm text-[#d7d0c8]">{currentCopy.enquire.emailLabel}</span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-white outline-none transition focus:border-[#C7A239]/60"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm text-[#d7d0c8]">{currentCopy.enquire.destination}</span>
                <select
                  name="destination"
                  value={form.destination}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-white outline-none transition focus:border-[#C7A239]/60"
                >
                  <option value="Saudi Arabia">{destinationOptionLabels.saudi}</option>
                  <option value="Uzbekistan">{destinationOptionLabels.uzbekistan}</option>
                  <option value="Both">{destinationOptionLabels.both}</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-2 block text-sm text-[#d7d0c8]">{currentCopy.enquire.travelMonth}</span>
                <input
                  type="text"
                  name="travelMonth"
                  value={form.travelMonth}
                  onChange={handleChange}
                  placeholder={currentCopy.enquire.travelMonthPlaceholder}
                  className="w-full rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-white outline-none transition focus:border-[#C7A239]/60"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm text-[#d7d0c8]">{currentCopy.enquire.guests}</span>
                <input
                  type="text"
                  name="guests"
                  value={form.guests}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-white outline-none transition focus:border-[#C7A239]/60"
                />
              </label>
            </div>

            <label className="mt-5 block">
              <span className="mb-2 block text-sm text-[#d7d0c8]">{currentCopy.enquire.experienceType}</span>
              <select
                name="experienceType"
                value={form.experienceType}
                onChange={handleChange}
                className="w-full rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-white outline-none transition focus:border-[#C7A239]/60"
              >
                {localizedExperienceTypes.map((type) => (
                  <option key={type}>{type}</option>
                ))}
              </select>
            </label>

            <label className="mt-5 block">
              <span className="mb-2 block text-sm text-[#d7d0c8]">{currentCopy.enquire.requirements}</span>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows="5"
                className="w-full rounded-2xl border border-white/10 bg-[#111] px-4 py-3 text-white outline-none transition focus:border-[#C7A239]/60"
                placeholder={currentCopy.enquire.requirementsPlaceholder}
              />
            </label>

            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C7A239] px-6 py-3.5 text-sm font-semibold text-black transition hover:bg-[#d8b14d]"
              >
                {currentCopy.enquire.submit}
              </button>
              <a
                href={heroWhatsapp}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-white hover:text-black"
              >
                <MessageCircle className="h-4 w-4" />
                {currentCopy.enquire.chat}
              </a>
            </div>
          </form>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#070707] px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionIntro
            eyebrow={currentCopy.faqIntro.eyebrow}
            title={currentCopy.faqIntro.title}
            body={currentCopy.faqIntro.body}
          />

          <div className="mt-12 grid gap-4">
            {localizedFaqs.map((faq) => (
              <details
                key={faq.question}
                className="group overflow-hidden rounded-[1.4rem] border border-white/10 bg-[#0b0b0b] px-6 py-5"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
                  <span className="font-['Playfair_Display'] text-2xl text-white">{faq.question}</span>
                  <CircleHelp className="h-5 w-5 shrink-0 text-[#C7A239] transition group-open:rotate-180" />
                </summary>
                <p className="mt-4 max-w-4xl text-sm leading-8 text-[#d4cec6]">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl grid gap-8 xl:grid-cols-[0.9fr_1.1fr] items-start">
          <SectionIntro
            eyebrow={currentCopy.relatedIntro.eyebrow}
            title={currentCopy.relatedIntro.title}
            body={currentCopy.relatedIntro.body}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { href: "/", label: "BlackRide homepage" },
              { href: "/chauffeur-service-saudi-arabia", label: "Premium chauffeur service in Saudi Arabia" },
              { href: "/airport-transfer-saudi-arabia", label: "Airport transfer in Saudi Arabia" },
              { href: "/jeddah-airport-transfer", label: "Jeddah airport transfer" },
              { href: "/jeddah-airport-to-makkah-transfer", label: "Jeddah Airport to Makkah private transfer" },
              { href: "/makkah-to-madinah-transfer", label: "Makkah to Madinah chauffeur service" },
              { href: "/umrah-transport-saudi-arabia", label: "Umrah transport in Saudi Arabia" },
              { href: "/blog", label: "BlackRide blog" },
            ].map((item, index) => (
              <Link
                key={item.href}
                to={item.href}
                className="rounded-[1.4rem] border border-white/10 bg-[#0b0b0b] px-5 py-5 text-sm text-[#ddd5cb] transition hover:border-[#C7A239]/40 hover:text-white"
              >
                {relatedServiceLabels?.[index] || item.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#050505] px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-6xl rounded-[2.2rem] border border-[#C7A239]/20 bg-[linear-gradient(135deg,rgba(199,162,57,0.12),rgba(255,255,255,0.02))] p-8 md:p-12 text-center shadow-[0_28px_90px_rgba(0,0,0,0.35)]">
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white leading-tight mb-5">
            {currentCopy.finalCta.title}
          </h2>
          <p className="mx-auto max-w-3xl text-[15px] md:text-lg leading-8 text-[#d9d1c7] mb-8">
            {currentCopy.finalCta.body}
          </p>
          <a
            href={heroWhatsapp}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C7A239] px-7 py-4 text-sm font-semibold text-black transition hover:bg-[#d8b14d]"
          >
            {currentCopy.finalCta.button}
          </a>
        </div>
      </section>

      <a
        href={heroWhatsapp}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-24 left-1/2 z-40 inline-flex -translate-x-1/2 items-center gap-2 rounded-full border border-[#C7A239]/30 bg-black/90 px-5 py-3 text-sm font-semibold text-[#E0C26A] shadow-[0_18px_60px_rgba(0,0,0,0.45)] md:hidden"
      >
        <MessageCircle className="h-4 w-4" />
        {currentCopy.stickyCta}
      </a>

      <Footer forceDark />
    </div>
  );
}
